/*
 * FOR SALE — filtres par jeu de la liste des annonces (section for-sale-demo, annonces Discord réelles).
 *
 * Module ES partagé : la première partie (« noyau ») est pure, sans DOM, importée telle quelle par les tests Node
 * (tools/listing-filters/*.test.mjs) ; la seconde (« interface ») ne s'exécute que dans un navigateur.
 *
 * CONTRAT
 * - Configuration : <script type="application/json" id="fs-listing-filters-config"> (snippet GÉNÉRÉ
 *   fs-listing-filters-data, depuis config/listing-filters/*.json par tools/listing-filters/build.mjs).
 * - Cartes : [data-fs-listing] portent data-game, data-fs-ref, data-fs-order, data-fs-posted (identifiant Discord daté),
 *   data-fs-level, data-fs-rank, data-fs-server, data-fs-tags (JSON) et data-fs-details (JSON) : valeurs brutes du
 *   métaobjet, normalisées ici. Prix (tris « Prix décroissant / croissant ») : data-fs-price (centimes) de l'élément du
 *   prix, lu seulement si la carte porte data-fs-price-state="priced" ; toute autre carte est « sans prix affiché ».
 *   Baisse affichée (facette de type « drop », « En baisse ») : data-fs-drop (pourcentage) et data-fs-drop-until (fin,
 *   secondes Unix) de la carte, posés par fs-listing-card et retirés par fs-price-drop.js à la fin de la baisse.
 * - Index complet : une carte complète par annonce admise (250 premiers métaobjets lus par la page, limite de paginate ;
 *   au-delà, data-fs-index-partial et un avertissement). Aucune carte chargée après coup : la Section Rendering API
 *   ignore ?page=N pour cette section (constat en production, 14 octobre 2026).
 * - Pagination : une page (12 / 8 cartes), puis défilement continu qui révèle les cartes déjà présentes (AUTO_PAGES pages
 *   au plus par état des filtres) et bouton « Voir plus d'annonces » en repli ; nombre de cartes rétabli au retour sur la
 *   liste (sessionStorage).
 * - Jeu courant : celui de l'écran, posé par for-sale.js (data-fs-catalog-game sur la racine : code du jeu ou « all »).
 *   Ce module ne fait que restreindre : une carte visible a data-game du jeu courant ET passe les facettes ; le résultat
 *   est porté par l'attribut hidden des [data-fs-offer] (contrat existant), réappliqué quand for-sale.js réécrit hidden
 *   (MutationObserver). data-fs-flt-out marque les cartes écartées par un filtre.
 * - Rubrique « Jeu » : liens rendus par la section (template[data-fs-flt-games] : « Tous les jeux », puis un par jeu,
 *   data-fs-pick-game, data-fs-total) ; choisir un jeu y entre par window.FSNav.pick (comme la tuile du portail).
 *   Vue « Tous les jeux » seulement : sur l'écran d'un jeu, on en change par « Changer de jeu » de l'en-tête.
 *   Au changement de jeu, les filtres propres au jeu repartent de zéro ; recherche gardée, tri gardé s'il existe.
 * - « Tous les jeux » : aiguillage (rubrique « Jeu », recherche, Nouveautés si les annonces sont datées, tri par prix
 *   si une annonce affiche un prix).
 * - Placement : colonne collante à droite de la grille à partir de 1 476 px (section : .fs-cat-layout), un seul
 *   défilement ; en dessous (cartes « Hangar » de 411 px à 1 440 px), dock collant et tiroir. Mêmes nœuds déplacés (place()), état et focus gardés.
 * - Adresse : ?f=<facette>:<valeur>;… &q=<recherche> &sort=<tri>, à côté de ?game= (routeur) ; toujours l'état
 *   affiché (history.state.fsFltGame : jeu des filtres de l'entrée). Précédent / Suivant : syncFromUrl().
 * - Aucune requête réseau. Écritures limitées à la barre [data-fs-flt], à l'outil posé avant la grille (.fs-flt-toolbar, bureau), à l'état vide
 *   et à la pagination posés après elle, aux attributs hidden / ordre des cartes et au
 *   texte du message « jeu sans annonce » de la section ; la zone [data-fs-commerce] des cartes affichées n'est jamais
 *   touchée.
 *   Icônes : celles du pack du jeu actif (décoratives).
 * - window.FSListingFilters : { state(), set(partial), reset(), syncFromUrl(), duration(), result(), reveal(ref),
 *   page() (limit, size, shown, total, auto), compare(refA, refB), comparable(game), measure(runs) }
 *   (routeur, recette, intégrations ; compare = face-à-face de fs-compare.js, cœur pur compareListings).
 * - Langue (épic INTL) : textes de l'interface par fs-i18n.js (zone i18n/filters.json) ; libellés de jeu de la
 *   configuration (facettes, options, groupes, rangs, tris) en { en, fr, es, tr, ru } ou en texte, choisis par
 *   localizeConfig() (pick : langue de la page, repli anglais) ; nombres par Intl dans la langue de la page.
 */
import { t, pick, number, locale } from './fs-i18n.js';

import { bindDialog } from './fs-ui-dialog.js';
import { el as h } from './fs-ui.js';

/* ============================== Noyau pur ============================== */

/* Libellés de jeu localisés de la configuration générée : un objet { en, fr, es, tr, ru } devient le texte de la langue
   de la page (repli anglais) ; un texte passe tel quel. Copie profonde, une seule fois par configuration. */
const LOCALE_KEYS = new Set(['en', 'fr', 'es', 'tr', 'ru']);
const isLocalized = (node) => Boolean(node) && typeof node === 'object' && !Array.isArray(node) && Object.hasOwn(node, 'en')
  && Object.keys(node).every((key) => LOCALE_KEYS.has(key)) && Object.values(node).every((value) => typeof value === 'string');
const localizedConfigs = new WeakMap();
export function localizeConfig(config) {
  if (!config || typeof config !== 'object') return config;
  if (localizedConfigs.has(config)) return localizedConfigs.get(config);
  const walk = (node) => {
    if (isLocalized(node)) return pick(node);
    if (Array.isArray(node)) return node.map(walk);
    if (node && typeof node === 'object') return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, walk(value)]));
    return node;
  };
  const out = walk(config);
  localizedConfigs.set(config, out);
  localizedConfigs.set(out, out);
  return out;
}

/** Clé normalisée, identique à tools/game-packs/pack.mjs (normalizeKey) : sans accents, minuscules, [a-z0-9]. */
export const normalizeKey = (label) => String(label ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/œ/g, 'oe').replace(/æ/g, 'ae').replace(/ß/g, 'ss')
  .replace(/[^a-z0-9]/g, '');

/**
 * Libellé affiché d'une étiquette (tags Discord du forum, au choix de chaque salon : « FULL DCS », « a-designs »,
 * « Starter ACC »…), une seule casse sur tout le site. Même règle, mot par mot, que le snippet fs-tag-label (carte,
 * fiche) et que tagLabel de fs-propose.js (aperçu) :
 * - mot avec un chiffre, ou sigle connu (DCS, DCH, ACC) : en capitales (« lg4 » → « LG4 ») ;
 * - mot tout en capitales de 4 caractères ou plus : initiale seule (« FULL » → « Full », « MONSTER » → « Monster ») ;
 * - autre mot inchangé (« Hyp-J », « Inferno ») ; puis première lettre du libellé en capitale (« a-designs » → « A-designs »).
 * La clé de filtre (normalizeKey) ne dépend pas de la casse : les filtres sont inchangés.
 */
const TAG_ACRONYMS = new Set(['dcs', 'dch', 'acc']);
export const tagLabel = (tag) => {
  const label = String(tag ?? '').trim().split(/\s+/).filter(Boolean).map((word) => {
    const upper = word.toUpperCase();
    const lower = word.toLowerCase();
    if (/[0-9]/.test(word) || TAG_ACRONYMS.has(lower)) return upper;
    if (word === upper && word !== lower && word.length > 3) return upper.slice(0, 1) + lower.slice(1);
    return word;
  }).join(' ');
  return label.slice(0, 1).toUpperCase() + label.slice(1);
};

/** Texte replié pour la recherche : sans accents, minuscules, mots séparés par une espace. */
export const foldText = (text) => String(text ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae')
  .replace(/[^a-z0-9]+/g, ' ').trim();

const YES_MARK = /[✅✔☑]/u; // ✅ ✔ ☑
const NO_MARK = /[❌❎✖✗✘⛔]/u; // ❌ ❎ ✖ ✗ ✘ ⛔
// Pastilles et pictogrammes décoratifs des modèles Discord (🔵 🟡 🔴, sélecteurs de variante…).
const DECOR = /[\u{1F000}-\u{1FAFF}\u{2190}-\u{2BFF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}]/gu;
const YES_WORDS = new Set(['yes', 'oui', 'true', 'vrai', 'available', 'disponible', 'dispo', 'ok', 'y']);
const NO_WORDS = new Set(['no', 'non', 'false', 'faux', 'none', 'aucun', 'aucune', 'unavailable', 'indisponible', 'n']);
const MULTIPLIERS = { k: 1e3, kk: 1e6, m: 1e6, mio: 1e6, mil: 1e6, md: 1e9, mrd: 1e9, b: 1e9, bn: 1e9 };
// Qualificatif isolé après un nombre (« 345 000 Permanent ») : le nombre reste lisible.
const QUALIFIERS = new Set(['permanent', 'environ', 'env', 'approx', 'about', 'ca', 'max', 'total', 'pts', 'pt', 'points']);
// « - », « / », « — » seuls : rien de publié (équivaut à ❌).
const NOTHING = /^\s*[-/–—]\s*$/;

const isBlank = (value) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/** ✅ / oui / yes → true ; ❌ / non / no / « - » → false ; sinon null (non interprétable). */
export function parseBoolean(raw) {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw > 0 : null;
  if (typeof raw !== 'string') return null;
  if (NOTHING.test(raw)) return false;
  const yes = YES_MARK.test(raw); const no = NO_MARK.test(raw);
  if (yes !== no) return yes;
  const key = normalizeKey(raw);
  if (YES_WORDS.has(key)) return true;
  if (NO_WORDS.has(key)) return false;
  return null;
}

/**
 * Nombre publié sur Discord : 140000, « 140 000 », « ~ 140.000 », « 200000~ », « 1,5M », « LVL20: », « 10 🔵 ».
 * ✅ / ❌ (booléens ou absence), « ~ », texte libre, groupements incohérents (« 44.5000 ») → null : la valeur est
 * exclue d'un filtre numérique actif, l'annonce reste visible quand le filtre est inactif.
 */
export function parseNumber(raw) {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  if (YES_MARK.test(raw) || NO_MARK.test(raw) || NOTHING.test(raw)) return null;
  let text = raw.replace(DECOR, ' ').toLowerCase().replace(/[~≈+]/g, ' ').replace(/\s+/g, ' ').trim();
  text = text.replace(/^(?:lvl|level|niveau|niv|lv)\.?\s*:?\s*/, '').replace(/\s*[:;.]+$/, '').trim();
  text = text.replace(/\s*[x×]$/, '');
  const words = /^(.*\d)\s+([a-z]+)$/.exec(text);
  if (words && QUALIFIERS.has(words[2])) text = words[1];
  if (!text) return null;
  if (/^\d+$/.test(text)) return Number(text);
  if (/^\d{1,3}([ .,'\u00a0\u202f])\d{3}(?:\1\d{3})*$/.test(text)) return Number(text.replace(/\D/g, ''));
  if (/^\d+[.,]\d{1,2}$/.test(text)) return Number(text.replace(',', '.'));
  const suffixed = /^(\d+(?:[.,]\d+)?)\s*(kk|k|mio|mil|mrd|md|m|bn|b)$/.exec(text);
  if (suffixed) return Math.round(Number(suffixed[1].replace(',', '.')) * MULTIPLIERS[suffixed[2]]);
  return null;
}

/** Durée Premium : « 22 Days », « Days 75 », « 30Days », « 1 jour », 45 → jours ; ❌ → 0 ; sinon null. */
export function parseDays(raw) {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? raw : null;
  if (typeof raw === 'boolean') return raw ? null : 0;
  if (typeof raw !== 'string') return null;
  if ((NO_MARK.test(raw) && !YES_MARK.test(raw)) || NOTHING.test(raw)) return 0;
  const text = raw.replace(DECOR, ' ').toLowerCase().replace(/\s+/g, ' ').trim();
  const match = /^(\d+)\s*(?:days?|jours?|j|d)?\.?$/.exec(text) || /^(?:days?|jours?)\s*:?\s*(\d+)$/.exec(text);
  return match ? Number(match[1]) : null;
}

/** Couverture « x / y » : « 8 / 8 🔵 », « 4/ 8 », « 7/8 » → { n, d } ; un nombre seul → { n, d: null }. */
export function parseRatio(raw) {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? { n: raw, d: null } : null;
  if (typeof raw !== 'string') return null;
  if (YES_MARK.test(raw) || NO_MARK.test(raw)) return null;
  const text = raw.replace(DECOR, ' ').replace(/\s+/g, ' ').trim();
  // Le compteur peut être suivi d'un commentaire (« 7 / 8 🔵 Ultrabolization … ») : seul le début compte.
  const match = /^(\d{1,3})\s*\/\s*(\d{1,3})(?=$|\s)/.exec(text);
  if (match) { const d = Number(match[2]); return d > 0 ? { n: Number(match[1]), d } : null; }
  return /^\d{1,3}$/.test(text) ? { n: Number(text), d: null } : null;
}

/**
 * Date de publication d'une annonce Discord : l'identifiant du message de départ est un « snowflake » horodaté
 * (millisecondes depuis 2015 dans ses 42 bits de poids fort). Identifiant absent ou non daté (annonce du site,
 * exemple) → null : l'annonce n'est jamais comptée comme nouvelle.
 */
const DISCORD_EPOCH = 1420070400000;
export function postedTime(posted) {
  const text = String(posted ?? '').trim();
  if (!/^\d{15,20}$/.test(text)) return null;
  const time = Number(BigInt(text) >> 22n) + DISCORD_EPOCH;
  return Number.isFinite(time) ? time : null;
}

/** Distance d'édition (fautes de frappe des rangs : captaim → captain). */
export function levenshtein(a, b) {
  if (a === b) return 0;
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    previous = current;
  }
  return previous[b.length];
}

/** Rang en casse libre → indice dans l'ordre du jeu (alias, puis faute de frappe tolérée), sinon null. */
export function normalizeRank(raw, facet) {
  const key = normalizeKey(raw);
  if (!key || !facet?.levels?.length) return null;
  const index = new Map(facet.levels.map((level, position) => [level.value, position]));
  const direct = index.get(facet.aliases?.[key] ?? key);
  if (direct !== undefined) return direct;
  const limit = key.length >= 6 ? 2 : key.length >= 4 ? 1 : 0;
  const candidates = [...facet.levels.map((level) => [level.value, level.value]), ...Object.entries(facet.aliases ?? {})];
  let best = null; let distance = Infinity; let ambiguous = false;
  for (const [candidate, target] of candidates) {
    const d = levenshtein(key, candidate);
    if (d < distance) { distance = d; best = target; ambiguous = false; }
    else if (d === distance && target !== best) ambiguous = true;
  }
  return distance <= limit && !ambiguous ? index.get(best) ?? null : null;
}

/**
 * Tris par prix, communs à tous les jeux (« Tous les jeux » compris), proposés dès qu'une annonce du jeu affiche un
 * prix. Identifiants avec un tiret : jamais en conflit avec un tri configuré ([a-z0-9], tools/listing-filters/build.mjs).
 */
export const PRICE_SORTS = Object.freeze([
  Object.freeze({ id: 'prix-desc', label: t('filters.sort_price_desc'), price: true }),
  Object.freeze({ id: 'prix-asc', label: t('filters.sort_price_asc'), price: true, dir: 'asc' }),
]);

/** Annonce prête à filtrer, depuis les valeurs brutes de la carte (attributs data-fs-*). */
export function prepareListing(raw = {}) {
  const details = new Map();
  if (raw.details && typeof raw.details === 'object' && !Array.isArray(raw.details)) {
    for (const [label, value] of Object.entries(raw.details)) {
      const key = normalizeKey(label);
      if (key && !details.has(key) && !isBlank(value)) details.set(key, value);
    }
  }
  const tags = (Array.isArray(raw.tags) ? raw.tags : [])
    .filter((tag) => typeof tag === 'string' && tag.trim())
    .map((tag) => ({ key: normalizeKey(tag), label: tagLabel(tag) }))
    .filter((tag) => tag.key);
  const ref = String(raw.ref ?? '').trim();
  const title = String(raw.title ?? '').trim();
  // Identifiant Discord du message de départ (horodaté, croissant dans le temps) ; vide si non daté (annonce du site).
  const posted = /^\d{15,20}$/.test(String(raw.posted ?? '').trim()) ? String(raw.posted).trim() : '';
  // Prix public en centimes (entier positif) ; sinon null : annonce sans prix affiché (bientôt, réservée, vendue,
  // prix sur demande, exemple « Pas à vendre »).
  const cents = typeof raw.price === 'number' || /^\s*\d{1,12}\s*$/.test(String(raw.price ?? '')) ? Number(raw.price) : NaN;
  const price = Number.isSafeInteger(cents) && cents > 0 ? cents : null;
  // Baisse affichée (data-fs-drop, data-fs-drop-until de la carte ; 028-baisse-prix-proportionnelle) : pourcentage entier
  // d'au moins 5 et fin en secondes Unix, lus seulement avec un prix affiché ; facette « drop » (« En baisse »).
  const dropRaw = String(raw.drop ?? '').trim(), untilRaw = String(raw.dropUntil ?? '').trim();
  const drop = price !== null && /^\d{1,2}$/.test(dropRaw) && Number(dropRaw) >= 1 && /^\d{9,11}$/.test(untilRaw)
    ? { percent: Number(dropRaw), until: Number(untilRaw) * 1000 } : null;
  return {
    ref, title, game: String(raw.game ?? ''), order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : 0, posted, price, drop,
    fields: { level: raw.level, rank: raw.rank, server: raw.server },
    details, tags, refKey: normalizeKey(ref), haystack: foldText(`${ref} ${title}`),
  };
}

const readSource = (facet, listing) => {
  const field = facet.source?.field;
  if (field && field !== 'tags' && !isBlank(listing.fields[field])) return listing.fields[field];
  for (const key of facet.source?.keys ?? []) if (listing.details.has(key)) return listing.details.get(key);
  return undefined;
};

/** Liste d'une caractéristique (« Goliath, Citadel » ou une valeur par ligne) en éléments { key, label }. */
const listItems = (raw) => (typeof raw === 'string' ? raw.split(/[,\n]/) : [])
  .map((label) => label.trim()).filter(Boolean).map((label) => ({ key: normalizeKey(label), label })).filter((item) => item.key);

const optionIndex = (options) => {
  const map = new Map();
  for (const option of options ?? []) for (const key of [option.value, ...(option.keys ?? [])]) if (!map.has(key)) map.set(key, option.value);
  return map;
};

const capitalise = (text) => (text === text.toUpperCase() || text === text.toLowerCase()
  ? text.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase()) : text);

/** Valeurs d'une facette pour chaque annonce, et méta (bornes, options présentes, masquage). now : date de référence (« Nouveautés »). */
function compileFacet(facet, listings, now = Date.now()) {
  const n = listings.length;
  const values = new Array(n).fill(null);
  const meta = { hidden: false };
  switch (facet.type) {
    case 'range': {
      // « Full » sans nombre (LG4 : tous les emplacements équipés) vaut facet.full.value ; un nombre publié prime toujours.
      const fullWords = new Set(facet.full?.words ?? []);
      const fullTags = new Set(facet.full?.tags ?? []);
      for (let i = 0; i < n; i += 1) {
        const raw = readSource(facet, listings[i]);
        values[i] = parseNumber(raw ?? null);
        if (values[i] === null && facet.full && (raw === undefined ? listings[i].tags.some((tag) => fullTags.has(tag.key)) : fullWords.has(normalizeKey(raw)))) values[i] = facet.full.value;
      }
      const steps = [...new Set(values.filter((value) => value !== null))].sort((a, b) => a - b);
      const buckets = Math.min(steps.length, 18);
      const position = new Map(steps.map((value, index) => [value, index]));
      meta.steps = steps;
      meta.buckets = buckets;
      meta.bucketOf = values.map((value) => (value === null ? -1 : Math.min(buckets - 1, Math.floor((position.get(value) * buckets) / steps.length))));
      meta.hidden = steps.length < 2;
      break;
    }
    case 'ratio': {
      const complete = new Set(facet.completeTags ?? []);
      for (let i = 0; i < n; i += 1) {
        const ratio = parseRatio(readSource(facet, listings[i]) ?? null);
        if (ratio) values[i] = { n: ratio.n, d: ratio.d ?? facet.of };
        else if (listings[i].tags.some((tag) => complete.has(tag.key))) values[i] = { n: facet.of, d: facet.of };
      }
      const present = values.filter(Boolean);
      const numerators = [...new Set(present.map((value) => Math.min(value.n, facet.of)).filter((value) => value > 0))];
      meta.thresholds = [...new Set([...numerators, facet.of])].sort((a, b) => a - b).slice(-4);
      // Masquée si elle ne sépare rien : aucune valeur, ou toutes les annonces dans le même état (absent compris).
      meta.hidden = present.length === 0 || new Set(values.map((value) => (value ? (value.n >= value.d ? 'full' : value.n) : 'none'))).size < 2;
      break;
    }
    case 'choice': {
      const index = optionIndex(facet.options);
      const dynamic = new Map();
      for (let i = 0; i < n; i += 1) {
        const raw = readSource(facet, listings[i]);
        const key = normalizeKey(raw);
        if (!key) continue;
        const value = index.get(key) ?? key;
        values[i] = value;
        if (!index.has(key) && !dynamic.has(key)) dynamic.set(key, { value: key, label: capitalise(String(raw).replace(DECOR, '').trim()) });
      }
      meta.options = optionsPresent(facet, dynamic, values.map((value) => (value === null ? [] : [value])));
      meta.hidden = meta.options.length < 2;
      break;
    }
    case 'tags': {
      const index = optionIndex(facet.options);
      const excluded = new Set(facet.exclude ?? []);
      const dynamic = new Map();
      for (let i = 0; i < n; i += 1) {
        const set = new Set();
        /* Étiquettes de l'annonce, ou liste d'une caractéristique (« Ships : Goliath, Citadel ») découpée aux virgules. */
        const items = facet.source?.field === 'tags' ? listings[i].tags : listItems(readSource(facet, listings[i]));
        for (const tag of items) {
          if (excluded.has(tag.key)) continue;
          const value = index.get(tag.key) ?? tag.key;
          set.add(value);
          if (!index.has(tag.key) && !dynamic.has(tag.key)) dynamic.set(tag.key, { value: tag.key, label: tag.label });
        }
        values[i] = set;
      }
      meta.options = optionsPresent(facet, dynamic, values.map((set) => [...set])).filter((option) => option.total < n);
      meta.hidden = meta.options.length === 0;
      break;
    }
    case 'flags': {
      const words = listings.map((listing) => foldText(listing.title).split(' '));
      for (let i = 0; i < n; i += 1) {
        const set = new Set();
        const tagKeys = new Set(listings[i].tags.map((tag) => tag.key));
        for (const option of facet.options ?? []) {
          const ok = (option.when ?? []).some((rule) => {
            if (rule.tag) return tagKeys.has(rule.tag);
            // Mot du titre accolé à un mot attendu : « Inferno Slipstream », « Punisher Inferno », « 2 Inferno Design ».
            if (rule.title) return words[i].some((word, at) => word === rule.title && (rule.beside.includes(words[i][at - 1]) || rule.beside.includes(words[i][at + 1])));
            const raw = (rule.keys ?? []).map((key) => listings[i].details.get(key)).find((value) => value !== undefined);
            const value = parseNumber(raw ?? null);
            return value !== null && value >= (rule.min ?? 1);
          });
          if (ok) set.add(option.value);
        }
        values[i] = set;
      }
      meta.options = optionsPresent(facet, new Map(), values.map((set) => [...set])).filter((option) => option.total < n);
      meta.hidden = meta.options.length === 0;
      break;
    }
    case 'bool': {
      for (let i = 0; i < n; i += 1) {
        const raw = readSource(facet, listings[i]);
        if (raw === undefined) continue;
        if (facet.parse === 'days') {
          const days = parseDays(raw);
          values[i] = days !== null ? days > 0 : parseBoolean(raw);
        } else values[i] = parseBoolean(raw);
      }
      const yes = values.filter((value) => value === true).length;
      meta.total = yes;
      meta.hidden = yes === 0 || yes === n;
      break;
    }
    case 'drop': {
      // « En baisse » : baisse affichée encore active à l'heure du visiteur (même règle que fs-price-drop.js) ; sans → false.
      for (let i = 0; i < n; i += 1) values[i] = Boolean(listings[i].drop && listings[i].drop.until > now);
      const yes = values.filter((value) => value === true).length;
      meta.total = yes;
      meta.hidden = yes === 0 || yes === n;
      break;
    }
    case 'recent': {
      // « Nouveautés » : publiée depuis facet.days jours (date du message Discord) ; non datée → null (jamais nouvelle).
      const span = (facet.days ?? 7) * 86400000;
      for (let i = 0; i < n; i += 1) {
        const time = postedTime(listings[i].posted);
        values[i] = time === null ? null : now - time <= span;
      }
      const yes = values.filter((value) => value === true).length;
      meta.total = yes;
      meta.hidden = yes === 0 || yes === n;
      break;
    }
    case 'rank': {
      for (let i = 0; i < n; i += 1) {
        const raw = readSource(facet, listings[i]);
        values[i] = raw === undefined ? null : normalizeRank(raw, facet);
      }
      const present = [...new Set(values.filter((value) => value !== null))].sort((a, b) => a - b);
      meta.levels = present.map((index) => ({ ...facet.levels[index], index }));
      meta.hidden = present.length < 2;
      break;
    }
    default:
      meta.hidden = true;
  }
  return { ...facet, values, meta };
}

function optionsPresent(facet, dynamic, lists) {
  const totals = new Map();
  for (const list of lists) for (const value of list) totals.set(value, (totals.get(value) ?? 0) + 1);
  const known = (facet.options ?? []).filter((option) => totals.get(option.value)).map((option) => ({ ...option, total: totals.get(option.value) }));
  const extra = [...dynamic.values()].filter((option) => totals.get(option.value))
    .map((option) => ({ ...option, total: totals.get(option.value) }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, locale));
  return [...known, ...extra];
}

const SET_TYPES = new Set(['choice', 'tags', 'flags']);

/** Valeur d'état valide et active pour une facette, sinon null. */
export function cleanValue(facet, value) {
  if (value === null || value === undefined || facet.meta?.hidden) return null;
  switch (facet.type) {
    case 'range': {
      const min = Number.isFinite(value?.min) ? value.min : null;
      const max = Number.isFinite(value?.max) ? value.max : null;
      if (min === null && max === null) return null;
      const steps = facet.meta.steps;
      const low = min !== null && min > steps[0] ? min : null;
      const high = max !== null && max < steps.at(-1) ? max : null;
      if (low === null && high === null) return null;
      if (low !== null && high !== null && low > high) return null;
      return { min: low, max: high };
    }
    case 'ratio': return Number.isInteger(value) && value >= 1 && value <= facet.of ? value : null;
    case 'bool': case 'recent': case 'drop': return value === true ? true : null;
    case 'rank': return facet.meta.levels.some((level) => level.value === value) ? value : null;
    default: {
      if (!SET_TYPES.has(facet.type)) return null;
      const allowed = new Set(facet.meta.options.map((option) => option.value));
      const list = [...new Set(Array.isArray(value) ? value : [])].filter((item) => allowed.has(item));
      return list.length ? list : null;
    }
  }
}

function predicate(facet, value) {
  switch (facet.type) {
    case 'range': return (v) => v !== null && (value.min === null || v >= value.min) && (value.max === null || v <= value.max);
    case 'ratio': return (v) => v !== null && (value >= facet.of ? v.n >= v.d && v.n >= facet.of : v.n >= value);
    case 'choice': { const set = new Set(value); return (v) => v !== null && set.has(v); }
    case 'tags': case 'flags': return (v) => value.every((item) => v.has(item));
    case 'bool': case 'recent': case 'drop': return (v) => v === true;
    case 'rank': { const min = facet.levels.findIndex((level) => level.value === value); return (v) => v !== null && v >= min; }
    default: return () => true;
  }
}

/**
 * Moteur d'un jeu : facettes compilées sur ses annonces. evaluate(state) → visibles, nombre, compteurs par option
 * (« combien si je coche ceci », les autres filtres appliqués), histogrammes, gains si l'on retire un filtre.
 * state = { q: string, sort: string, f: { [facette]: valeur } }. options.now : date de référence des « Nouveautés ».
 */
export function createEngine(rawConfig, rawListings, { now = Date.now() } = {}) {
  const config = localizeConfig(rawConfig);
  const listings = rawListings.map((item) => (item?.details instanceof Map ? item : prepareListing(item)));
  const facets = (config?.facets ?? []).map((facet) => compileFacet(facet, listings, now));
  const byId = new Map(facets.map((facet) => [facet.id, facet]));
  const visibleFacets = facets.filter((facet) => !facet.meta.hidden);
  const sorts = [
    ...(listings.some((listing) => listing.price !== null) ? PRICE_SORTS : []),
    ...(config?.sorts ?? []).filter((sort) => byId.get(sort.facet)?.type === 'range' && !byId.get(sort.facet).meta.hidden),
  ];

  const normalizeState = (state = {}) => {
    const f = {};
    for (const facet of visibleFacets) {
      const value = cleanValue(facet, state.f?.[facet.id]);
      if (value !== null) f[facet.id] = value;
    }
    const q = typeof state.q === 'string' ? state.q.slice(0, 80) : '';
    const sort = sorts.some((item) => item.id === state.sort) ? state.sort : '';
    return { q, sort, f };
  };

  const evaluate = (rawState) => {
    const state = normalizeState(rawState);
    const n = listings.length;
    const active = visibleFacets.filter((facet) => state.f[facet.id] !== undefined);
    const tests = active.map((facet) => predicate(facet, state.f[facet.id]));
    const tokens = foldText(state.q).split(' ').filter(Boolean);
    const searchSlot = tokens.length ? active.length : -1;
    const fails = new Uint8Array(n);
    const first = new Int16Array(n).fill(-1);
    for (let slot = 0; slot < active.length; slot += 1) {
      const values = active[slot].values; const test = tests[slot];
      for (let i = 0; i < n; i += 1) if (!test(values[i])) { fails[i] += 1; if (first[i] < 0) first[i] = slot; }
    }
    if (searchSlot >= 0) {
      for (let i = 0; i < n; i += 1) {
        const hay = listings[i].haystack; const ref = listings[i].refKey;
        if (!tokens.every((token) => hay.includes(token) || ref.includes(token))) { fails[i] += 1; if (first[i] < 0) first[i] = searchSlot; }
      }
    }
    const visible = Array.from(fails, (count) => count === 0);
    const count = visible.filter(Boolean).length;
    const others = (i, slot) => fails[i] === 0 || (slot >= 0 && fails[i] === 1 && first[i] === slot);

    const counts = {};
    for (const facet of visibleFacets) {
      const slot = active.indexOf(facet);
      const values = facet.values;
      if (facet.type === 'range') {
        const hist = new Array(facet.meta.buckets).fill(0);
        for (let i = 0; i < n; i += 1) if (values[i] !== null && others(i, slot)) hist[facet.meta.bucketOf[i]] += 1;
        counts[facet.id] = { hist };
      } else if (facet.type === 'ratio') {
        const map = {};
        for (const k of facet.meta.thresholds) map[k] = 0;
        for (let i = 0; i < n; i += 1) {
          const v = values[i];
          if (!v || !others(i, slot)) continue;
          for (const k of facet.meta.thresholds) if (k >= facet.of ? v.n >= v.d && v.n >= facet.of : v.n >= k) map[k] += 1;
        }
        counts[facet.id] = map;
      } else if (facet.type === 'choice') {
        const map = {};
        for (let i = 0; i < n; i += 1) if (values[i] !== null && others(i, slot)) map[values[i]] = (map[values[i]] ?? 0) + 1;
        counts[facet.id] = map;
      } else if (facet.type === 'tags' || facet.type === 'flags') {
        // Toutes les options cochées doivent être présentes : « combien si j'ajoute celle-ci » = visibles qui l'ont.
        const map = {};
        for (let i = 0; i < n; i += 1) if (fails[i] === 0) for (const value of values[i]) map[value] = (map[value] ?? 0) + 1;
        counts[facet.id] = map;
      } else if (facet.type === 'bool' || facet.type === 'recent' || facet.type === 'drop') {
        let yes = 0;
        for (let i = 0; i < n; i += 1) if (values[i] === true && others(i, slot)) yes += 1;
        counts[facet.id] = { true: yes };
      } else if (facet.type === 'rank') {
        const at = new Array(facet.levels.length).fill(0);
        for (let i = 0; i < n; i += 1) if (values[i] !== null && others(i, slot)) at[values[i]] += 1;
        const map = {};
        let running = 0;
        for (let index = at.length - 1; index >= 0; index -= 1) { running += at[index]; map[facet.levels[index].value] = running; }
        counts[facet.id] = map;
      }
    }
    const gains = [...active.map((facet, slot) => ({ id: facet.id, slot })), ...(searchSlot >= 0 ? [{ id: 'q', slot: searchSlot }] : [])]
      .map(({ id, slot }) => {
        let total = 0;
        for (let i = 0; i < n; i += 1) if (others(i, slot)) total += 1;
        return { id, count: total };
      })
      .sort((a, b) => b.count - a.count);
    return { state, visible, count, total: n, counts, gains, active: active.map((facet) => facet.id) };
  };

  // Tri par défaut honnête : « Plus récentes » (identifiant Discord) dès qu'une annonce est datée, les annonces non
  // datées après, dans l'ordre publié ; sinon « Ordre de publication » (ordre rendu par la boutique).
  const recent = listings.some((listing) => listing.posted);
  const defaultSortLabel = recent ? t('filters.sort_recent') : t('filters.sort_published');
  const newer = (a, b) => {
    const pa = listings[a].posted; const pb = listings[b].posted;
    if (!pa || !pb) return (!pa) - (!pb);
    return pb.length - pa.length || (pb > pa ? 1 : pb < pa ? -1 : 0);
  };

  /** Ordre d'affichage (indices) : tri choisi (prix ou facette), valeurs absentes à la fin ; défaut = plus récentes (ou ordre publié), référence exacte d'abord. */
  const order = (rawState) => {
    const state = normalizeState(rawState);
    const indices = listings.map((_, index) => index);
    const sort = sorts.find((item) => item.id === state.sort);
    if (sort?.price) {
      // Prix : annonces à prix d'abord (dans le sens choisi), puis sans prix affiché, dans les deux sens ; à égalité,
      // plus récentes d'abord, puis ordre publié (stable).
      const direction = sort.dir === 'asc' ? 1 : -1;
      return indices.sort((a, b) => {
        const pa = listings[a].price; const pb = listings[b].price;
        const byPrice = pa === null || pb === null ? (pa === null) - (pb === null) : (pa - pb) * direction;
        return byPrice || (recent ? newer(a, b) : 0) || listings[a].order - listings[b].order;
      });
    }
    if (sort) {
      const values = byId.get(sort.facet).values;
      const direction = sort.dir === 'asc' ? 1 : -1;
      return indices.sort((a, b) => {
        const va = values[a]; const vb = values[b];
        if (va === null || vb === null) return (va === null) - (vb === null) || listings[a].order - listings[b].order;
        return (va - vb) * direction || listings[a].order - listings[b].order;
      });
    }
    const query = normalizeKey(state.q);
    const score = (listing) => {
      if (!query) return 0;
      if (listing.refKey === query) return 0;
      if (listing.refKey.startsWith(query)) return 1;
      if (listing.refKey.includes(query)) return 2;
      return 3;
    };
    return indices.sort((a, b) => score(listings[a]) - score(listings[b]) || (recent ? newer(a, b) : 0) || listings[a].order - listings[b].order);
  };

  return { listings, facets, visibleFacets, byId, sorts, groups: config?.groups ?? [], normalizeState, evaluate, order, recent, defaultSortLabel };
}

/** ?f= : « level:18-21;lg4:40-;faction:orion,vega;premium:1;rank:major;dcs:8 ». */
export function encodeFilters(engine, rawState) {
  const { f } = engine.normalizeState(rawState);
  return engine.visibleFacets.filter((facet) => f[facet.id] !== undefined).map((facet) => {
    const value = f[facet.id];
    switch (facet.type) {
      case 'range': return `${facet.id}:${value.min ?? ''}-${value.max ?? ''}`;
      case 'bool': case 'recent': case 'drop': return `${facet.id}:1`;
      case 'ratio': case 'rank': return `${facet.id}:${value}`;
      default: return `${facet.id}:${value.join(',')}`;
    }
  }).join(';');
}

export function decodeFilters(engine, text) {
  const f = {};
  for (const part of String(text ?? '').slice(0, 600).split(';')) {
    const colon = part.indexOf(':');
    if (colon < 1) continue;
    const facet = engine.byId.get(part.slice(0, colon));
    const raw = part.slice(colon + 1);
    if (!facet) continue;
    let value = null;
    switch (facet.type) {
      case 'range': {
        const match = /^(-?\d+(?:\.\d+)?)?-(-?\d+(?:\.\d+)?)?$/.exec(raw);
        if (match) value = { min: match[1] === undefined ? null : Number(match[1]), max: match[2] === undefined ? null : Number(match[2]) };
        break;
      }
      case 'ratio': value = /^\d{1,3}$/.test(raw) ? Number(raw) : null; break;
      case 'bool': case 'recent': case 'drop': value = raw === '1' ? true : null; break;
      case 'rank': value = raw; break;
      default: value = raw.split(',').filter(Boolean);
    }
    const clean = cleanValue(facet, value);
    if (clean !== null) f[facet.id] = clean;
  }
  return f;
}

/** 1 250 000 → « 1,3 M » (compact) ou « 1 250 000 » (langue de la page : « 1.3M », « 1,250,000 » en anglais) ;
    espaces insécables classiques (polices des jeux). */
export const formatValue = (value, format) => number(value, (format === 'compact' && Math.abs(value) >= 10000)
  ? { notation: 'compact', maximumFractionDigits: 1 } : { maximumFractionDigits: 2 }).replace(/\u202f/g, '\u00a0');

/** Libellé court d'un filtre actif (puces, état vide). */
export function describeValue(facet, value) {
  switch (facet.type) {
    case 'range': {
      const fmt = (v) => formatValue(v, facet.format);
      if (value.min !== null && value.max !== null) return `${facet.label} ${fmt(value.min)} – ${fmt(value.max)}`;
      return value.min !== null ? `${facet.label} ≥ ${fmt(value.min)}` : `${facet.label} ≤ ${fmt(value.max)}`;
    }
    case 'ratio': return value >= facet.of ? `${facet.label} ${facet.of}/${facet.of}` : `${facet.label} ≥ ${value}/${facet.of}`;
    case 'bool': case 'recent': case 'drop': return facet.label;
    case 'rank': return t('filters.rank_min', { label: facet.levels.find((level) => level.value === value)?.label ?? value });
    default: return value.map((item) => facet.meta.options.find((option) => option.value === item)?.label ?? item).join(', ');
  }
}

/* ============================== Comparaison ============================== */

/** Libellé d'une caractéristique dans le face-à-face (sans le vocabulaire des filtres : « Grade minimum » → « Grade »). */
const compareLabel = (facet) => facet.compareLabel ?? String(facet.label ?? facet.id).replace(/\s+minimum$/i, '');

/** Libellés lisibles des éléments d'une liste (étiquettes, vaisseaux, designs) : options connues, sinon texte publié. */
function itemLabels(facet, pair) {
  const labels = new Map((facet.options ?? []).map((option) => [option.value, option.label]));
  const index = optionIndex(facet.options);
  for (const listing of pair) {
    const items = facet.type === 'flags' ? [] : facet.source?.field === 'tags' ? listing.tags : listItems(readSource(facet, listing));
    for (const item of items) {
      const value = index.get(item.key) ?? item.key;
      if (!labels.has(value)) labels.set(value, capitalise(String(item.label).replace(DECOR, '').trim()));
    }
  }
  return labels;
}

/** Proportion d'une barre du face-à-face, entre 0 et 1, au millième. */
const share = (value) => Math.round(Math.min(1, Math.max(0, value)) * 1000) / 1000;

/**
 * Ligne du face-à-face pour une facette compilée sur [A, B] ; null si aucune des deux annonces ne la renseigne.
 * bars : longueurs des deux barres en miroir (0 à 1), seulement quand les deux valeurs sont connues et ordonnables :
 * nombre = part de la plus grande des deux ; ratio = n / d ; rang = position dans l'échelle du jeu ((index + 1) / N) ;
 * oui / non = 1 / 0. Sinon null (valeur absente, choix, liste) : aucune barre, comme aucun gagnant.
 */
function compareRow(facet, pair) {
  const [a, b] = facet.values;
  const base = { id: facet.id, label: compareLabel(facet), icon: facet.icon ?? '', type: facet.type, bars: null };
  const order = (x, y) => (x > y ? 'a' : y > x ? 'b' : 'tie');
  switch (facet.type) {
    case 'range': {
      if (a === null && b === null) return null;
      const show = (v) => (v === null ? null : formatValue(v, facet.format === 'compact' ? 'plain' : facet.format));
      const both = a !== null && b !== null;
      const top = both ? Math.max(a, b, 0) : 0;
      const bars = both ? { a: top > 0 ? share(a / top) : 0, b: top > 0 ? share(b / top) : 0 } : null;
      return { ...base, a: show(a), b: show(b), winner: both ? order(a, b) : null, delta: both && a !== b ? formatValue(Math.abs(a - b), 'plain') : null, bars };
    }
    case 'ratio': {
      if (!a && !b) return null;
      const show = (v) => (v ? `${v.n}/${v.d}` : null);
      const both = Boolean(a && b);
      const delta = both && a.d === b.d && a.n !== b.n ? String(Math.abs(a.n - b.n)) : null;
      return { ...base, a: show(a), b: show(b), winner: both ? order(a.n / a.d, b.n / b.d) : null, delta, bars: both ? { a: share(a.n / a.d), b: share(b.n / b.d) } : null };
    }
    case 'rank': {
      if (a === null && b === null) return null;
      const show = (v) => (v === null ? null : facet.levels[v]?.label ?? null);
      const both = a !== null && b !== null;
      const steps = both ? Math.abs(a - b) : 0;
      return { ...base, a: show(a), b: show(b), iconA: a === null ? '' : facet.levels[a]?.icon ?? '', iconB: b === null ? '' : facet.levels[b]?.icon ?? '',
        winner: both ? order(a, b) : null, delta: steps ? t('filters.rank_steps', { count: steps }) : null,
        bars: both && facet.levels.length ? { a: share((a + 1) / facet.levels.length), b: share((b + 1) / facet.levels.length) } : null };
    }
    case 'bool': {
      if (a === null && b === null) return null;
      const show = (v) => (v === null ? null : v ? t('listing.yes') : t('listing.no'));
      const both = a !== null && b !== null;
      return { ...base, a: show(a), b: show(b), winner: both ? order(Number(a), Number(b)) : null, delta: null, bars: both ? { a: a ? 1 : 0, b: b ? 1 : 0 } : null };
    }
    case 'choice': {
      if (a === null && b === null) return null;
      const labels = itemLabels(facet, pair);
      const show = (v) => (v === null ? null : labels.get(v) ?? capitalise(v));
      return { ...base, a: show(a), b: show(b), winner: null, same: a !== null && a === b, delta: null };
    }
    case 'flags':
    case 'tags': {
      if (!a?.size && !b?.size) return null;
      const labels = itemLabels(facet, pair);
      const name = (v) => labels.get(v) ?? capitalise(v);
      const list = (set, other) => [...set].filter((v) => !other.has(v)).map(name);
      return { ...base, winner: null, delta: null, a: a.size ? String(a.size) : null, b: b.size ? String(b.size) : null,
        common: [...a].filter((v) => b.has(v)).map(name), onlyA: list(a, b), onlyB: list(b, a) };
    }
    default:
      return null;
  }
}

/**
 * Face-à-face de deux annonces d'un même jeu : une ligne par caractéristique renseignée chez au moins l'une des deux,
 * dans les groupes et l'ordre des filtres du jeu. Gagnant seulement quand les deux valeurs sont connues et ordonnables
 * (nombre, ratio, rang, oui/non) : une valeur absente reste « non précisée », jamais comptée comme nulle. Les choix
 * (faction, serveur) et les listes (designs, vaisseaux) ne désignent pas de gagnant. Étiquettes libres exclues.
 * Retour : { groups: [{ id, label, rows }], tally: { a, b, tie } } ; chaque ligne porte bars ({ a, b } de 0 à 1, ou null).
 */
export function compareListings(rawConfig, rawA, rawB) {
  const config = localizeConfig(rawConfig);
  const pair = [rawA, rawB].map((item) => (item?.details instanceof Map ? item : prepareListing(item)));
  const groups = new Map((config?.groups ?? []).map((group) => [group.id, { id: group.id, label: group.label, rows: [] }]));
  const other = { id: 'autres', label: t('filters.other_specs'), rows: [] };
  const tally = { a: 0, b: 0, tie: 0 };
  for (const facet of config?.facets ?? []) {
    if (facet.compare === false || facet.type === 'recent' || facet.type === 'drop' || (facet.type === 'tags' && facet.source?.field === 'tags')) continue;
    const row = compareRow(compileFacet(facet, pair), pair);
    if (!row) continue;
    if (row.winner) tally[row.winner] += 1;
    (groups.get(facet.group) ?? other).rows.push(row);
  }
  return { groups: [...groups.values(), other].filter((group) => group.rows.length), tally };
}

/**
 * Texte traduit dont certaines variables sont mises en forme (gras) : [texte, { bold: valeur }, texte…], sans HTML.
 * Chaque variable mise en forme est remplacée par un repère à l'interpolation (toString) mais garde sa valeur pour le
 * choix du pluriel (valueOf) : « 1 listing », « 5 объявлений », jamais le pluriel « other » par défaut.
 */
export function richParts(key, params, bold) {
  const marks = Object.fromEntries(bold.map((name) => [name, { valueOf: () => params[name], toString: () => `\u0000${name}\u0000` }]));
  return t(key, { ...params, ...marks }).split('\u0000').map((part, index) => (index % 2 ? { bold: String(params[part]) } : part)).filter((part) => part !== '');
}

/* ---------- Pagination : cartes de la page révélées par pages, défilement continu ---------- */

/** Pages révélées d'elles-mêmes au défilement après chaque changement de filtre, puis bouton seul (pied de page atteint). */
export const AUTO_PAGES = 3;

/**
 * Défilement continu permis ? Sentinelle proche du bas de la grille, cartes restantes, moins de AUTO_PAGES pages révélées
 * ainsi depuis le dernier changement de filtre, pas de mouvement réduit, et un visiteur qui fait défiler la liste
 * lui-même (intention « read » de fs-scroll.js). Un trajet (« navigate » : lien vers #sell, ancre, retour, arrivée par
 * l'adresse) ne fait que traverser la liste : révéler des cartes pendant ce trajet pousse la cible plus bas et le
 * défilement s'arrête court (« Sell an account » à refaire deux fois, 30 octobre 2026).
 */
export function shouldAutoLoad({ near = false, rest = 0, auto = 0, reduced = false, navigating = false } = {}) {
  return Boolean(near) && rest > 0 && auto < AUTO_PAGES && !reduced && !navigating;
}

/**
 * Nombre de cartes à rétablir au retour sur la liste (sessionStorage, par adresse et état des filtres) : entrée de moins
 * de `maxAge` ms pour ce même `key`, bornée à [size, total] ; sinon null.
 */
export function restoredLimit(saved, { key, size, total, now = Date.now(), maxAge = 30 * 60_000 } = {}) {
  const entry = saved && typeof saved === 'object' ? saved[key] : null;
  if (!entry || typeof entry !== 'object') return null;
  const limit = Number(entry.limit);
  const at = Number(entry.at);
  if (!Number.isSafeInteger(limit) || !Number.isFinite(at) || now - at > maxAge || now < at) return null;
  if (limit <= size) return null;
  return Math.min(limit, Math.max(size, total));
}

/** Entrées gardées en session après l'ajout de `key` : les `max` plus récentes. */
export function rememberLimit(saved, key, limit, { now = Date.now(), max = 12 } = {}) {
  const entries = Object.entries(saved && typeof saved === 'object' ? saved : {}).filter(([name]) => name !== key);
  if (Number.isSafeInteger(limit) && limit > 0) entries.push([key, { limit, at: now }]);
  return Object.fromEntries(entries.sort((a, b) => Number(a[1]?.at || 0) - Number(b[1]?.at || 0)).slice(-max));
}

/* ---------- Travail regroupé par image, reporté après le défilement ---------- */

/** Défilement tenu pour fini après ce délai sans événement « scroll » (repli de « scrollend »). */
export const SCROLL_IDLE_MS = 150;

/** Rang de la première différence entre deux suites (−1 : identiques). */
export function firstDifference(a, b) {
  const n = Math.max(a.length, b.length);
  for (let index = 0; index < n; index += 1) if (a[index] !== b[index]) return index;
  return -1;
}

/**
 * Regroupe des demandes en un seul passage par image : request() autant de fois qu'on veut, run({ scrolling }) une fois à
 * l'image suivante. run répond 'defer' pour attendre la fin du défilement : nouveau passage à « scrollend »
 * (scrollEnded) ou après `idle` ms sans « scroll » (scrolled), puis à l'image suivante. Les demandes arrivées entre-temps
 * sont fondues dans ce passage. scrolling : un « scroll » depuis moins de `idle` ms, sans « scrollend » depuis.
 * Minuteries injectées (tests Node) : frame / cancelFrame (requestAnimationFrame), wait / cancelWait (setTimeout), now.
 */
export function createFrameBatch(run, { frame, cancelFrame, wait, cancelWait, now, idle = SCROLL_IDLE_MS }) {
  let pending = false;
  let frameId = 0;
  let timer = 0;
  let waiting = false;
  let last = -Infinity;
  let ended = true;
  const scrolling = () => !ended && now() - last < idle;
  const tick = () => {
    frameId = 0;
    if (!pending || waiting) return;
    pending = false;
    if (run({ scrolling: scrolling() }) === 'defer') { pending = true; waiting = true; arm(); }
  };
  const schedule = () => { if (!frameId && !waiting) frameId = frame(tick); };
  const wake = () => {
    if (timer) cancelWait(timer);
    timer = 0;
    waiting = false;
    if (pending) schedule();
  };
  // Repli : `idle` ms après le dernier « scroll » (réarmé à chacun).
  const arm = () => { if (timer) cancelWait(timer); timer = wait(() => { timer = 0; ended = true; wake(); }, idle); };
  return {
    request() { pending = true; schedule(); },
    scrolled() { last = now(); ended = false; if (waiting) arm(); },
    scrollEnded() { ended = true; if (waiting) wake(); },
    cancel() { pending = false; waiting = false; if (frameId) cancelFrame(frameId); if (timer) cancelWait(timer); frameId = 0; timer = 0; },
    get pending() { return pending; },
    get waiting() { return waiting; },
    get scrolling() { return scrolling(); },
  };
}

/**
 * Met les éléments de `sequence` dans cet ordre, à la fin de `parent` : même résultat que parent.append(...sequence)
 * (les autres enfants gardent leur ordre, devant), mais seuls les éléments mal placés sont déplacés (de la fin vers le
 * début : une carte qui descend coûte un déplacement). Un nœud déplacé est retiré puis réinséré (observateurs, focus) :
 * on en déplace le moins possible. Retour : nombre de déplacements.
 */
export function placeSequence(parent, sequence) {
  let next = null;
  let moves = 0;
  for (let index = sequence.length - 1; index >= 0; index -= 1) {
    const node = sequence[index];
    if (node.parentNode !== parent || node.nextElementSibling !== next) { parent.insertBefore(node, next); moves += 1; }
    next = node;
  }
  return moves;
}

/* ============================== Interface ============================== */

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const reducedMotion = () => document.documentElement.dataset.fsMotion === 'reduced'
    || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  /* Nombre d'annonces dans la langue de la page (« 3 annonces », « 3 listings », « 3 объявления »). */
  const listingsText = (count) => t('filters.listings', { count });
  /* Texte traduit dont certaines variables sont en gras (nœuds <b>, jamais de HTML). */
  const rich = (key, params, bold) => richParts(key, params, bold).map((part) => (typeof part === 'string' ? part : h('b', { text: part.bold })));
  /* Pictogramme SVG statique (balisage constant du module, jamais de donnée d'annonce). */
  const glyph = (className, markup) => {
    const box = document.createElement('span');
    box.innerHTML = `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${markup}</svg>`;
    return box.firstChild;
  };
  const SEARCH_GLYPH = '<circle cx="10.5" cy="10.5" r="6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M15 15l5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>';
  let uid = 0;
  const nextId = (prefix) => `fs-flt-${prefix}-${(uid += 1)}`;
  /* Facettes à nombreuses options (vaisseaux, serveurs) : au-delà de MANY options, les FEW plus fréquentes (chipsControl). */
  const MANY_OPTIONS = 10;
  const FEW_OPTIONS = 8;
  /* Colonne latérale à partir de cette largeur (grand bureau) ; en dessous, dock collant et tiroir (mobile, tablette,
     bureau jusqu'à 1 475 px : la colonne de 256 px réduirait les cartes de 411 à 320 px à 1 440 px). */
  const SIDE_QUERY = '(min-width: 1476px)';
  /* Rubrique « Jeu » : au-delà de GAMES_SHOWN jeux, les plus fournis et le jeu courant, puis « Voir les N jeux ». */
  const GAMES_SHOWN = 6;
  const plainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const boot = (root) => {
    const host = root.querySelector('[data-fs-flt]');
    const grid = root.querySelector('[data-fs-offer-grid]');
    if (!host || !grid || host.hasAttribute('data-fs-flt-ready')) return;
    let config;
    try { config = localizeConfig(JSON.parse(document.getElementById('fs-listing-filters-config')?.textContent || 'null')); } catch { config = null; }
    if (!config?.games) return;
    const cards = [...grid.querySelectorAll('[data-fs-listing]')];
    if (!cards.length) return;
    const json = (card, name) => { try { return JSON.parse(card.getAttribute(name) || 'null'); } catch { return null; } };
    const prepared = cards.map((card, index) => {
      const listing = prepareListing({
        ref: card.dataset.fsRef, title: card.querySelector('.fs-lc-title')?.textContent, game: card.dataset.game,
        order: card.dataset.fsOrder || index + 1, level: card.dataset.fsLevel, rank: card.dataset.fsRank,
        server: card.dataset.fsServer, tags: json(card, 'data-fs-tags'), details: json(card, 'data-fs-details'),
        posted: card.dataset.fsPosted,
        price: card.dataset.fsPriceState === 'priced' ? card.querySelector('[data-fs-price]')?.dataset.fsPrice : null,
        drop: card.dataset.fsDrop, dropUntil: card.dataset.fsDropUntil,
      });
      listing.card = card;
      return listing;
    });

    /* Moteur d'un jeu (« all » : filtres communs). Un jeu sans annonce a un moteur vide : barre et rubrique « Jeu »
       restent en place (mise en page stable, changement de jeu direct), le message de la section dit qu'il n'y a rien. */
    const engines = new Map();
    const engineFor = (game) => {
      if (!engines.has(game)) {
        const list = game === 'all' ? prepared : prepared.filter((listing) => listing.game === game);
        engines.set(game, createEngine(game === 'all' ? config.common : config.games[game] ?? config.common, list));
      }
      return engines.get(game);
    };
    /* Jeu courant : celui que for-sale.js pose sur la racine (data-fs-catalog-game : jeu de l'écran, ou « all »), sinon
       le jeu de l'écran (data-fs-universe). Un écran de jeu sans annonce garde son code : aucune carte d'un autre jeu
       n'est réaffichée. */
    const currentGame = () => {
      const catalog = root.dataset.fsCatalogGame;
      if (catalog) return catalog;
      const universe = root.getAttribute('data-fs-universe') || '';
      return universe && universe !== 'default' ? universe : 'all';
    };

    /* ---------- Rubrique « Jeu » (persistante) ----------
       « Tous les jeux » puis un lien par jeu (logo, nom, annonces publiées), rendus par la section
       (template[data-fs-flt-games], ordre des tuiles du portail). Choisir un jeu = y entrer comme par sa tuile
       (habillage, filtres du jeu, ?game=, nouvelle entrée d'historique : window.FSNav.pick), sans remonter en haut de
       page. Jamais reconstruite au changement de jeu : le lien choisi garde le focus. Bureau : tête de la colonne ;
       mobile et tablette : tête du tiroir, ouverte par la puce « Jeu ▾ » du dock. Vue « Tous les jeux » seulement :
       sur l'écran d'un jeu, l'en-tête porte déjà « Changer de jeu » ([data-fs-portal-open], retour au portail) et la
       puce du dock (nom du jeu) mène au même endroit. */
    const gameLinks = [...(root.querySelector('template[data-fs-flt-games]')?.content.querySelectorAll('a[data-fs-pick-game]') ?? [])]
      .map((link) => document.importNode(link, true));
    const linkOf = (code) => gameLinks.find((link) => link.dataset.fsPickGame === code);
    const gameName = (id) => (linkOf(id)?.querySelector('.fs-flt-game-name')?.textContent
      || root.querySelector(`[data-fs-gate="${CSS.escape(id)}"] .fs-home-game-name`)?.textContent || id).trim();
    /* Annonces publiées d'un jeu (plafond d'affichage par jeu compris), données par la section. */
    const totalOf = (code) => Number(linkOf(code)?.dataset.fsTotal);
    const otherGames = gameLinks.filter((link) => link.dataset.fsPickGame !== 'all');
    const manyGames = otherGames.length > GAMES_SHOWN;
    // Rang figé au chargement (aucun saut) : annonces décroissantes, puis ordre du portail.
    const topGames = new Set([...otherGames].sort((a, b) => (Number(b.dataset.fsTotal) || 0) - (Number(a.dataset.fsTotal) || 0))
      .slice(0, GAMES_SHOWN - 1).map((link) => link.dataset.fsPickGame));
    let gamesExpanded = false;
    const gamesTitle = nextId('jeu');
    const gamesMore = h('button', { type: 'button', class: 'fs-flt-showmore', 'aria-expanded': 'false', hidden: true });
    const gameNav = otherGames.length ? h('nav', { class: 'fs-flt-games', 'aria-labelledby': gamesTitle, 'data-fs-flt-facet': 'jeu' },
      h('p', { class: 'fs-flt-caption fs-flt-games-title', id: gamesTitle }, h('span', { class: 'fs-flt-caption-text', text: t('filters.game') })),
      h('ul', { class: 'fs-flt-games-list', role: 'list' }, gameLinks.map((link) => h('li', {}, link))), gamesMore) : null;
    const gameChip = gameNav ? h('button', { type: 'button', class: 'fs-chip fs-flt-chip fs-flt-chip--game', 'aria-haspopup': 'dialog' }) : null;
    gamesMore.addEventListener('click', () => { gamesExpanded = !gamesExpanded; syncGames(); });
    const gamesShown = () => Boolean(gameNav) && game === 'all';
    gameChip?.addEventListener('click', () => {
      if (gamesShown()) { openSheet('jeu'); return; }
      const portal = document.querySelector('[data-fs-portal-open]');
      if (portal) portal.click(); else pickGame('all', linkOf('all') ?? gameChip);
    });
    gameNav?.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[data-fs-pick-game]');
      if (!link || !plainClick(event)) return;
      event.preventDefault();
      pickGame(link.dataset.fsPickGame, link);
    });
    function syncGames() {
      if (!gameNav) return;
      for (const link of gameLinks) {
        const code = link.dataset.fsPickGame;
        if (code === game) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
        link.parentElement.hidden = manyGames && !gamesExpanded && code !== 'all' && code !== game && !topGames.has(code);
      }
      gamesMore.hidden = !manyGames;
      gamesMore.setAttribute('aria-expanded', String(gamesExpanded));
      gamesMore.textContent = gamesExpanded ? t('filters.see_less') : t('filters.see_games', { count: otherGames.length });
      const name = game === 'all' ? '' : gameName(game);
      const mark = name ? linkOf(game)?.querySelector('.fs-flt-game-mark')?.cloneNode(true) : null;
      gameChip.replaceChildren(...[mark, h('span', { class: 'fs-chip-label fs-flt-chip-label', text: name || t('filters.game') }), h('span', { class: 'fs-flt-opener-chevron', 'aria-hidden': 'true' })].filter(Boolean));
      gameChip.setAttribute('aria-label', name ? t('filters.game_current', { game: name }) : t('filters.game_choose'));
      // Écran d'un jeu : la puce ne s'ouvre plus sur le tiroir, elle ramène au choix du jeu (comme l'en-tête).
      if (gamesShown()) gameChip.setAttribute('aria-haspopup', 'dialog'); else gameChip.removeAttribute('aria-haspopup');
    }
    /* Adresse de chaque lien (clic milieu, copie) : écran du jeu, recherche et tri gardés. */
    const syncGameHrefs = () => {
      if (typeof window.FSNav?.href !== 'function') return;
      for (const link of gameLinks) {
        const code = link.dataset.fsPickGame;
        try { link.href = window.FSNav.href({ univers: code === 'all' ? '' : code, hash: '#fs-catalog' }, { keepFilters: ['q', 'sort'] }); } catch { /* adresse de la section */ }
      }
    };
    /* Choix d'un jeu : l'entrée quittée garde son état exact (adresse écrite tout de suite), puis le routeur entre dans
       le jeu. Depuis le tiroir ouvert avec son entrée d'historique : le tiroir se ferme d'abord (retour à l'entrée d'avant,
       réécrite avec les filtres choisis dans le tiroir, voir le popstate plus bas), puis le jeu est choisi par un push
       normal ; Précédent ramène ainsi au jeu quitté avec ses filtres. */
    let pendingPick = null;
    function pickGame(code, link) {
      if (code === game) { if (ui?.sheet.open) ui.sheet.close(); return; }
      if (typeof window.FSNav?.pick !== 'function') { window.location.assign(link.href); return; }
      writeUrl();
      if (ui?.sheet.open) {
        if (sheetEntry && entryState().fsFltSheet) { pendingPick = code; ui.sheet.close(); return; }
        ui.sheet.close();
      }
      gamePending = true;
      window.FSNav.pick(code, { from: host.hasAttribute('data-fs-flt-side') ? link : gameChip });
    }

    /* Pagination : 12 cartes (bureau), 8 (mobile) par page, puis défilement continu et « Voir plus d'annonces » (plus bas).
       Remise à zéro à chaque changement de filtre. */
    const narrow = typeof matchMedia === 'function' ? matchMedia('(max-width: 767px)') : { matches: false };
    const pageSize = () => (narrow.matches ? 8 : 12);
    /* Bureau : outil (recherche, tri, nombre) en tête des résultats, hors de l'emplacement [data-fs-flt] (comme l'état
       vide et la pagination, posés à côté de la grille). */
    const wide = typeof matchMedia === 'function' ? matchMedia(SIDE_QUERY) : { matches: false, addEventListener() {} };
    const sideMode = () => wide.matches && Boolean(root.querySelector('[data-fs-cat-layout]'));
    const toolbar = h('div', { class: 'fs-flt fs-flt-toolbar', role: 'search', 'aria-label': t('filters.toolbar'), 'data-fs-flt-ready': '', hidden: true });
    grid.before(toolbar);
    /* Annonce des résultats (lecteurs d'écran) : persistante, différée pour ne pas bavarder ; nomme le jeu après un changement. */
    const live = h('p', { class: 'fs-visually-hidden', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
    let limit = pageSize();
    let pageKey = '';
    const packIcons = (game) => {
      if (game === 'all') return null;
      const read = (node) => { try { return node ? JSON.parse(node.textContent) : null; } catch { return null; } };
      const data = window.FSGameSkin?.data?.(game) || read(document.getElementById(`fs-game-pack-${game}`))
        || read(root.querySelector(`template[data-fs-pack-template="${CSS.escape(game)}"]`)?.content.querySelector(`#fs-game-pack-${game}`));
      return data?.icons ?? null;
    };

    let game = null;
    let engine = null;
    let state = { q: '', sort: '', f: {} };
    let icons = null;
    let controls = [];
    let ui = null;
    let lastResult = null;
    let lastDuration = 0;
    let orderKey = '';
    let booted = false;
    // Icônes des raccourcis du dock chargées tout de suite (peu nombreuses) ; celles de la colonne et du tiroir, différées.
    let iconLoading = 'lazy';

    const icon = (key, size = 22) => {
      const entry = key ? icons?.[key] : null;
      if (!entry || typeof entry.url !== 'string' || !/^(https?:)?\/\/|^\//.test(entry.url)) return null;
      return h('img', { class: 'fs-flt-icon', src: entry.url, alt: '', width: size, height: size, loading: iconLoading, decoding: 'async' });
    };
    /* Un nœud déplacé ou reconstruit perd le focus : on le lui rend s'il est toujours dans la page. */
    const keepFocus = (focused) => {
      if (focused && focused !== document.body && focused !== document.activeElement && focused.isConnected) focused.focus({ preventScroll: true });
    };

    /* ---------- Adresse : toujours l'état affiché (?f, ?q, ?tri, à côté de ?game=) ----------
       L'entrée d'historique retient le jeu de ses filtres (history.state.fsFltGame) : au changement de jeu, ?f n'est relu
       que s'il appartient au nouveau jeu (retour d'historique) ; sinon les filtres propres au jeu repartent de zéro. */
    let urlTimer = 0;
    const entryState = () => (history.state && typeof history.state === 'object' ? history.state : {});
    function writeUrl() {
      clearTimeout(urlTimer);
      try {
        const url = new URL(window.location.href);
        const put = (name, value) => (value ? url.searchParams.set(name, value) : url.searchParams.delete(name));
        put('f', encodeFilters(engine, state)); put('q', state.q.trim()); put('sort', ({ 'prix-asc': 'price-asc', 'prix-desc': 'price-desc' })[state.sort] || state.sort);
        if (url.href !== window.location.href || entryState().fsFltGame !== game) history.replaceState({ ...entryState(), fsFltGame: game }, '', url.toString());
      } catch { /* adresse inchangée */ }
      syncGameHrefs();
    }
    const scheduleUrl = () => { clearTimeout(urlTimer); urlTimer = setTimeout(writeUrl, 250); };
    const urlState = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        return engine.normalizeState({ q: params.get('q') || '', sort: ({ 'price-asc': 'prix-asc', 'price-desc': 'prix-desc' })[params.get('sort')] || params.get('sort') || params.get('tri') || '', f: decodeFilters(engine, params.get('f') || '') });
      } catch { return { q: '', sort: '', f: {} }; }
    };

    /* ---------- Annonce du nombre de résultats ---------- */
    let liveTimer = 0;
    let liveText = '';
    let gamePending = false;
    const announce = (text) => {
      liveText = text;
      clearTimeout(liveTimer);
      liveTimer = setTimeout(() => {
        live.textContent = gamePending ? t('filters.game_said', { game: gameName(game), text: liveText }) : liveText;
        gamePending = false;
      }, 450);
    };

    /* ---------- Contrôles ---------- */
    const setValue = (facet, value) => {
      const clean = cleanValue(facet, value);
      const f = { ...state.f };
      if (clean === null) delete f[facet.id]; else f[facet.id] = clean;
      state = { ...state, f };
      refresh();
    };

    const chip = ({ label, iconKey, count, pressed, onClick, extraClass = '' }) => {
      const number = h('span', { class: 'fs-flt-n fs-chip-count', 'aria-hidden': 'true' });
      const toggle = extraClass.includes('fs-flt-chip--toggle');
      // Puce du catalogue commun (fs-ui-tabs.css) ; interrupteur : case dessinée du catalogue (.fs-box).
      const button = h('button', { type: 'button', class: `fs-chip${toggle ? ' fs-chip--toggle' : ''} fs-flt-chip ${extraClass}`.trim(), 'aria-pressed': String(Boolean(pressed)) },
        icon(iconKey), toggle ? h('span', { class: 'fs-box', 'aria-hidden': 'true' }) : null, h('span', { class: 'fs-chip-label fs-flt-chip-label', text: label }), number);
      button.addEventListener('click', () => { if (button.getAttribute('aria-disabled') !== 'true') onClick(); });
      const update = (isPressed, total) => {
        button.setAttribute('aria-pressed', String(Boolean(isPressed)));
        number.textContent = total === undefined ? '' : String(total);
        const off = !isPressed && total === 0;
        button.setAttribute('aria-disabled', String(off));
        button.setAttribute('aria-label', total === undefined ? label : t('filters.with_count', { label, count: listingsText(total) }));
      };
      update(pressed, count);
      return { el: button, update };
    };

    const caption = (facet, id, extra) => h('p', { class: 'fs-flt-caption', id },
      icon(facet.icon, 20), h('span', { class: 'fs-flt-caption-text', text: facet.label }),
      facet.hint ? h('span', { class: 'fs-flt-hint', text: facet.hint }) : null, extra ?? null);

    const rangeControl = (facet) => {
      const steps = facet.meta.steps;
      const last = steps.length - 1;
      const id = nextId(facet.id);
      const out = h('output', { class: 'fs-flt-range-out', 'aria-hidden': 'true' });
      const hist = h('div', { class: 'fs-flt-hist', 'aria-hidden': 'true' }, Array.from({ length: facet.meta.buckets }, () => h('span')));
      const fill = h('div', { class: 'fs-flt-range-fill', 'aria-hidden': 'true' });
      const fmt = (index) => formatValue(steps[index], facet.format);
      const input = (which) => h('input', {
        type: 'range', class: `fs-flt-thumb fs-flt-thumb--${which}`, min: 0, max: last, step: 1,
        'aria-label': t(which === 'min' ? 'filters.range_min' : 'filters.range_max', { label: facet.label }),
      });
      const low = input('min'); const high = input('max');
      const indexOf = (value, fallback) => {
        if (value === null || value === undefined) return fallback;
        const found = steps.findIndex((step) => step >= value);
        return found < 0 ? last : found;
      };
      const read = () => {
        let a = Number(low.value); let b = Number(high.value);
        if (a > b) { if (document.activeElement === low) a = b; else b = a; low.value = String(a); high.value = String(b); }
        return [a, b];
      };
      const onInput = () => {
        const [a, b] = read();
        paint(a, b);
        setValue(facet, { min: a === 0 ? null : steps[a], max: b === last ? null : steps[b] });
      };
      low.addEventListener('input', onInput); high.addEventListener('input', onInput);
      const paint = (a, b) => {
        out.textContent = a === 0 && b === last ? `${fmt(0)} – ${fmt(last)}` : `${fmt(a)} – ${fmt(b)}`;
        low.setAttribute('aria-valuetext', fmt(a)); high.setAttribute('aria-valuetext', fmt(b));
        fill.style.setProperty('--a', String(a / last));
        fill.style.setProperty('--b', String(b / last));
        const bucketLow = Math.floor((a * facet.meta.buckets) / steps.length);
        const bucketHigh = Math.min(facet.meta.buckets - 1, Math.floor((b * facet.meta.buckets) / steps.length));
        [...hist.children].forEach((bar, index) => bar.classList.toggle('is-in', index >= bucketLow && index <= bucketHigh));
      };
      const wrap = h('div', { class: 'fs-flt-facet fs-flt-range', role: 'group', 'aria-labelledby': id },
        caption(facet, id, out),
        h('div', { class: 'fs-flt-range-track' }, hist, h('div', { class: 'fs-flt-range-rail', 'aria-hidden': 'true' }), fill, low, high),
        h('div', { class: 'fs-flt-range-ends', 'aria-hidden': 'true' }, h('span', { text: fmt(0) }), h('span', { text: fmt(last) })));
      const update = (result) => {
        const value = result.state.f[facet.id];
        const a = indexOf(value?.min, 0);
        let b = last;
        if (value?.max !== null && value?.max !== undefined) { while (b > a && steps[b] > value.max) b -= 1; }
        if (document.activeElement !== low && document.activeElement !== high) { low.value = String(a); high.value = String(b); }
        paint(Number(low.value), Number(high.value));
        const bars = result.counts[facet.id]?.hist ?? [];
        const max = Math.max(1, ...bars);
        [...hist.children].forEach((bar, index) => bar.style.setProperty('--h', `${Math.round(((bars[index] ?? 0) / max) * 100)}%`));
      };
      return { el: wrap, update };
    };

    const chipsControl = (facet, { compact = false } = {}) => {
      const id = nextId(facet.id);
      const list = h('div', { class: 'fs-flt-chips' });
      const items = [];
      const multiple = facet.type !== 'rank' && facet.type !== 'ratio';
      const options = facet.type === 'rank' ? facet.meta.levels
        : facet.type === 'ratio' ? facet.meta.thresholds.map((k) => ({ value: k, label: k >= facet.of ? `${facet.of}/${facet.of}` : `≥ ${k}/${facet.of}` }))
          : facet.meta.options;
      for (const option of options) {
        const item = chip({
          label: option.label, iconKey: option.icon,
          onClick: () => {
            const current = state.f[facet.id];
            if (!multiple) setValue(facet, current === option.value ? null : option.value);
            else {
              const set = new Set(current ?? []);
              if (set.has(option.value)) set.delete(option.value); else set.add(option.value);
              setValue(facet, [...set]);
            }
          },
        });
        items.push({ option, item });
        list.append(item.el);
      }
      /* Forte cardinalité (vaisseaux du hangar : 82 options DarkOrbit, 48 Nova Horizon ; serveurs : 24) : au-delà de
         MANY options, les FEW plus fréquentes (rang figé au chargement, pas de saut pendant le filtrage) et celles
         cochées, un champ « Chercher » qui filtre les libellés (sans accents), et « Voir les N ». */
      const many = !compact && options.length > MANY_OPTIONS;
      let expanded = false;
      let needle = '';
      const top = many ? new Set([...options].sort((a, b) => (b.total ?? 0) - (a.total ?? 0)).slice(0, FEW_OPTIONS).map((option) => option.value)) : null;
      const findId = many ? nextId(`${facet.id}-chercher`) : null;
      const finder = many ? h('input', {
        id: findId, type: 'search', class: 'fs-flt-find', autocomplete: 'off', spellcheck: 'false', enterkeyhint: 'search',
        placeholder: t('filters.find_among', { count: options.length }),
      }) : null;
      const noMatch = many ? h('p', { class: 'fs-flt-nomatch', hidden: true }) : null;
      const showMore = many ? h('button', { type: 'button', class: 'fs-flt-showmore', 'aria-expanded': 'false' }) : null;
      const narrowList = () => {
        if (!many) return;
        const value = state.f[facet.id];
        const query = foldText(needle);
        let shown = 0;
        for (const { option, item } of items) {
          const selected = multiple ? (value ?? []).includes(option.value) : value === option.value;
          const visible = query ? foldText(option.label).includes(query) : expanded || selected || top.has(option.value);
          if (item.el.hidden === visible) item.el.hidden = !visible;
          if (visible) shown += 1;
        }
        noMatch.hidden = !(query && shown === 0);
        noMatch.textContent = query ? t('filters.no_result_for', { query: needle.trim() }) : '';
        showMore.hidden = Boolean(query);
        showMore.setAttribute('aria-expanded', String(expanded));
        showMore.textContent = expanded ? t('filters.see_less') : t('filters.see_all_n', { count: options.length });
      };
      if (many) {
        finder.addEventListener('input', () => { needle = finder.value; narrowList(); });
        finder.addEventListener('keydown', (event) => { if (event.key === 'Enter') event.preventDefault(); });
        showMore.addEventListener('click', () => { expanded = !expanded; narrowList(); });
      }
      const wrap = h('div', { class: `fs-flt-facet fs-flt-choice${compact ? ' fs-flt-choice--compact' : ''}${many ? ' fs-flt-choice--many' : ''}`, role: 'group', 'aria-labelledby': id },
        caption(facet, id, facet.type === 'rank' ? h('span', { class: 'fs-flt-hint', text: t('filters.and_above') }) : null),
        many ? h('div', { class: 'fs-flt-find-wrap' }, h('label', { class: 'fs-visually-hidden', for: findId, text: t('filters.find_in', { label: facet.label }) }), finder) : null,
        list, noMatch, showMore);
      const update = (result) => {
        const value = result.state.f[facet.id];
        const counts = result.counts[facet.id] ?? {};
        for (const { option, item } of items) {
          const pressed = multiple ? (value ?? []).includes(option.value) : value === option.value;
          item.update(pressed, counts[option.value] ?? 0);
        }
        narrowList();
      };
      return { el: wrap, update };
    };

    /* Interrupteur (oui/non, « Nouveautés », ratio complet) ; quick : puce seule (raccourci du dock). */
    const toggleControl = (facet, { quick = false } = {}) => {
      const isRatio = facet.type === 'ratio';
      const label = isRatio ? `${facet.label} ${facet.of}/${facet.of}` : facet.label;
      const item = chip({
        label, iconKey: facet.icon, extraClass: 'fs-flt-chip--toggle',
        onClick: () => {
          const current = state.f[facet.id];
          if (isRatio) setValue(facet, current === facet.of ? null : facet.of);
          else setValue(facet, current === true ? null : true);
        },
      });
      if (facet.hint && isRatio) item.el.title = facet.hint;
      const update = (result) => {
        const value = result.state.f[facet.id];
        const counts = result.counts[facet.id] ?? {};
        if (isRatio) item.update(value === facet.of, counts[facet.of] ?? 0);
        else item.update(value === true, counts.true ?? 0);
      };
      return { el: quick ? item.el : h('div', { class: 'fs-flt-facet fs-flt-toggle' }, item.el), update };
    };

    const controlFor = (facet) => {
      switch (facet.type) {
        case 'range': return rangeControl(facet);
        case 'ratio': return chipsControl(facet);
        case 'bool': case 'recent': case 'drop': return toggleControl(facet);
        default: return chipsControl(facet);
      }
    };

    /* ---------- Barre ----------
       Un seul jeu de contrôles, reconstruit à chaque changement de jeu, puis placé selon la largeur (place()) :
       - grand bureau (≥ 1 476 px) : colonne collante à droite de la grille (rubrique « Jeu », puces actives, groupes repliables)
         et outil au-dessus de la grille (recherche, tri, nombre) ;
       - en dessous : recherche, puis dock collant (« Filtres » avec le nombre d'annonces, « Jeu ▾ », tri, filtres
         actifs, raccourcis) et tiroir (bas sur mobile, latéral sur tablette) : rubrique « Jeu », sommaire, groupes. */
    const build = () => {
      const focused = document.activeElement;
      controls = [];
      host.replaceChildren();
      toolbar.replaceChildren();
      grid.parentElement.querySelector(':scope > .fs-flt-empty')?.remove();
      icons = packIcons(game);
      host.setAttribute('data-fs-flt-game', game);
      const searchId = nextId('q');
      const sortId = nextId('tri');
      const sheetTitle = nextId('titre');
      const search = h('input', {
        id: searchId, type: 'search', class: 'fs-input fs-input--compact fs-input--icon fs-flt-search-input', autocomplete: 'off', spellcheck: 'false', enterkeyhint: 'search',
        placeholder: t('filters.search_placeholder'), value: state.q,
      });
      search.value = state.q;
      search.addEventListener('input', () => { state = { ...state, q: search.value }; refresh(); });
      search.addEventListener('keydown', (event) => { if (event.key === 'Enter') event.preventDefault(); });
      const sort = h('select', { id: sortId, class: 'fs-select fs-select--compact fs-select--auto fs-flt-sort-select' },
        h('option', { value: '', text: engine.defaultSortLabel }),
        engine.sorts.map((item) => h('option', { value: item.id, text: item.price ? item.label : t('filters.sort_desc', { label: item.label }) })));
      sort.value = state.sort;
      sort.addEventListener('change', () => { state = { ...state, sort: sort.value }; refresh(); });
      // span (pas p) : sur mobile et tablette, le nombre est placé dans le bouton « Filtres » du dock.
      const visibleCount = h('span', { class: 'fs-flt-count', 'aria-hidden': 'true' });
      const badge = h('span', { class: 'fs-flt-badge', 'aria-hidden': 'true' });
      const more = h('button', { type: 'button', class: 'fs-flt-more fs-btn', 'aria-haspopup': 'dialog' },
        glyph('fs-flt-more-icon', '<path d="M4 7h10M18 7h2M4 17h4M12 17h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/><circle cx="16" cy="7" r="2.2" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="10" cy="17" r="2.2" fill="none" stroke="currentColor" stroke-width="1.8"/>'),
        h('span', { class: 'fs-flt-more-label', text: t('filters.filters') }), badge);
      const searchWrap = h('div', { class: 'fs-flt-search fs-input-box' }, h('label', { class: 'fs-visually-hidden', for: searchId, text: t('filters.search_label') }), glyph('fs-input-icon fs-flt-search-icon', SEARCH_GLYPH), search);
      // Tri : un seul ordre possible (aucun prix affiché ni tri du jeu) → pas de sélecteur. Dock : puce « ⇅ Trier · <tri> », select natif transparent par-dessus.
      const sortCurrent = h('span', { class: 'fs-flt-sort-current', 'aria-hidden': 'true' });
      const sortWrap = h('div', { class: 'fs-flt-sort', hidden: engine.sorts.length === 0 },
        glyph('fs-flt-sort-icon', '<path d="M8 5v14M8 5l-3 3M8 5l3 3M16 19V5M16 19l-3-3M16 19l3-3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'),
        h('label', { class: 'fs-flt-sort-label', for: sortId, text: t('filters.sort') }), sortCurrent, sort);
      /* « Nouveautés · 7 jours (N) » (direction « Hangar », greffe Éditorial) : la facette « recent » de la vue (même
         règle que le badge « Nouveau » des cartes, fs-new-badge.js) en raccourci de la barre d'outils, avec son nombre.
         Bureau seulement : sur mobile et tablette, la même facette est déjà un raccourci du dock. */
      const recentFacet = engine.visibleFacets.find((facet) => facet.type === 'recent');
      let news = null;
      if (recentFacet) {
        const days = Number.isFinite(recentFacet.days) ? recentFacet.days : 7;
        const label = t('filters.news', { count: days });
        const newsCount = h('span', { class: 'fs-flt-news-n', 'aria-hidden': 'true' });
        news = h('button', { type: 'button', class: 'fs-chip fs-flt-chip fs-flt-news', 'aria-pressed': 'false' },
          h('span', { class: 'fs-flt-news-dot', 'aria-hidden': 'true' }), h('span', { class: 'fs-chip-label fs-flt-chip-label', text: label }), ' ', newsCount);
        news.addEventListener('click', () => {
          if (news.getAttribute('aria-disabled') === 'true') return;
          setValue(recentFacet, state.f[recentFacet.id] === true ? null : true);
        });
        controls.push({ update: (result) => {
          const on = result.state.f[recentFacet.id] === true;
          const total = result.counts[recentFacet.id]?.true ?? 0;
          newsCount.textContent = `(${total})`;
          news.setAttribute('aria-pressed', String(on));
          news.setAttribute('aria-disabled', String(!on && total === 0));
          news.setAttribute('aria-label', t('filters.news_label', { count: days, listings: listingsText(total) }));
        } });
      }
      const top = h('div', { class: 'fs-flt-top' });
      const pills = h('div', { class: 'fs-flt-active', role: 'group', 'aria-label': t('filters.active') });

      /* Dock : raccourcis du jeu (interrupteurs et choix principaux en direct ; plages et listes en « Niveau ▾ », qui
         ouvre le tiroir sur ce filtre), puis « N filtres ». */
      const suggest = h('div', { class: 'fs-flt-suggest', role: 'group', 'aria-label': t('filters.quick') });
      const openers = [];
      // Raccourcis : filtres principaux, puis ceux des groupes ouverts au chargement ou marqués shortcuts (Drones, replié).
      const groupOpen = (facet) => { const group = engine.groups.find((item) => item.id === facet.group); return Boolean(group?.open || group?.shortcuts); };
      const toggles = new Set(['bool', 'recent', 'drop', 'ratio']);
      // Ordre : ce qui filtre d'un toucher (interrupteurs, choix principaux) d'abord, puis les « Niveau ▾ » qui ouvrent le tiroir.
      const direct = (facet) => toggles.has(facet.type) || (facet.primary && ['choice', 'flags', 'tags'].includes(facet.type));
      const pool = [...engine.visibleFacets.filter((facet) => facet.primary), ...engine.visibleFacets.filter((facet) => !facet.primary && groupOpen(facet))];
      const candidates = [...pool.filter(direct), ...pool.filter((facet) => !direct(facet))];
      let shortcuts = 0;
      iconLoading = 'eager';
      for (const facet of candidates) {
        if (shortcuts >= 10) break;
        if (toggles.has(facet.type)) {
          const control = toggleControl(facet, { quick: true }); controls.push(control); suggest.append(control.el);
        } else if (facet.primary && ['choice', 'flags', 'tags'].includes(facet.type)) {
          const control = chipsControl(facet, { compact: true }); controls.push(control); suggest.append(control.el);
        } else if (facet.primary || facet.type !== 'range') {
          const button = h('button', { type: 'button', class: 'fs-chip fs-flt-chip fs-flt-chip--opener', 'aria-haspopup': 'dialog', 'aria-label': t('filters.opener', { label: facet.label }) },
            icon(facet.icon, 20), h('span', { class: 'fs-chip-label fs-flt-chip-label', text: facet.label }), h('span', { class: 'fs-flt-opener-chevron', 'aria-hidden': 'true' }));
          button.addEventListener('click', () => openSheet(facet.id));
          openers.push({ facet, button });
          suggest.append(button);
        } else continue;
        shortcuts += 1;
      }
      iconLoading = 'lazy';
      controls.push({ update: (result) => { for (const { facet, button } of openers) button.hidden = result.state.f[facet.id] !== undefined; } });
      const allFilters = engine.visibleFacets.length > shortcuts
        ? h('button', { type: 'button', class: 'fs-chip fs-flt-chip fs-flt-chip--all', 'aria-haspopup': 'dialog', text: t('filters.filters_n', { count: engine.visibleFacets.length }) }) : null;
      allFilters?.addEventListener('click', () => openSheet());
      const rail = h('div', { class: 'fs-flt-rail' });
      /* Fondu de droite (« il y a une suite », fs-filters.css) : une puce qui y entre garde son cadre mais masque son
         texte et son icône (data-fs-flt-edge), jamais un libellé à moitié effacé, quelle que soit la longueur des
         libellés de la langue. Recalculé au défilement de la rangée, au redimensionnement et à chaque rendu. */
      let edgeFrame = 0;
      const markEdge = () => {
        edgeFrame = 0;
        if (!rail.isConnected) return;
        const box = rail.getBoundingClientRect();
        const fade = box.right - (parseFloat(getComputedStyle(rail).getPropertyValue('--flt-fade')) || 28);
        rail.querySelectorAll('.fs-flt-chip, .fs-flt-pill, .fs-flt-sort, .fs-flt-reset-all').forEach((item) => {
          const right = item.getBoundingClientRect().right;
          item.toggleAttribute('data-fs-flt-edge', box.width > 0 && right > fade + 1);
        });
      };
      const edgeSoon = () => { if (!edgeFrame) edgeFrame = requestAnimationFrame(markEdge); };
      rail.addEventListener('scroll', edgeSoon, { passive: true });
      if (typeof ResizeObserver === 'function') new ResizeObserver(edgeSoon).observe(rail);
      controls.push({ update: edgeSoon });
      const dockSearch = h('button', { type: 'button', class: 'fs-flt-dock-search', 'aria-label': t('filters.search_open') }, glyph('fs-flt-dock-search-icon', SEARCH_GLYPH));
      const dock = h('div', { class: 'fs-flt-dock' });

      // Groupes repliables : groupe marqué open, ou portant un filtre actif (adresse partagée), ouvert d'emblée.
      const navItems = [];
      if (gamesShown()) {
        const jump = h('button', { type: 'button', class: 'fs-flt-groupnav-item' }, h('span', { text: t('filters.game') }));
        jump.addEventListener('click', () => { scrollSheetTo(gameNav); (gameNav.querySelector('[aria-current="page"]') ?? gameNav.querySelector('a'))?.focus({ preventScroll: true }); });
        navItems.push(jump);
      }
      const groups = engine.groups.map((group) => {
        const facets = engine.visibleFacets.filter((facet) => facet.group === group.id);
        if (!facets.length) return null;
        const count = h('span', { class: 'fs-flt-badge', 'aria-hidden': 'true' });
        const body = h('div', { class: 'fs-flt-group-body' });
        for (const facet of facets) { const control = controlFor(facet); control.el.setAttribute('data-fs-flt-facet', facet.id); controls.push(control); body.append(control.el); }
        const details = h('details', { class: 'fs-flt-group', open: group.open || facets.some((facet) => state.f[facet.id] !== undefined) ? true : null },
          h('summary', { class: 'fs-flt-group-head' }, h('span', { class: 'fs-flt-group-label', text: group.label }), count, h('span', { class: 'fs-flt-group-chevron', 'aria-hidden': 'true' })), body);
        // Sommaire du tiroir : un bouton par groupe (nombre de filtres actifs), ouvre et amène le groupe.
        const navCount = h('span', { class: 'fs-flt-badge', 'aria-hidden': 'true' });
        const navButton = h('button', { type: 'button', class: 'fs-flt-groupnav-item' }, h('span', { text: group.label }), navCount);
        navButton.addEventListener('click', () => { details.open = true; scrollSheetTo(details); details.querySelector('summary')?.focus({ preventScroll: true }); });
        navItems.push(navButton);
        controls.push({ update: (result) => {
          const n = facets.filter((facet) => result.state.f[facet.id] !== undefined).length;
          count.textContent = n ? String(n) : ''; navCount.textContent = count.textContent;
          navButton.setAttribute('aria-label', n ? t('filters.with_count', { label: group.label, count: t('filters.filters_active', { count: n }) }) : group.label);
        } });
        return details;
      }).filter(Boolean);
      const groupNav = navItems.length > 2 ? h('nav', { class: 'fs-flt-groupnav', 'aria-label': t('filters.groups') }, navItems) : null;
      // « Tous les jeux » : aiguillage. Les filtres propres à un jeu (niveau, drones, designs…) s'obtiennent en le choisissant.
      const groupsBox = h('div', { class: 'fs-flt-groups' },
        game === 'all' && gameNav ? h('p', { class: 'fs-flt-scope-hint', text: t('filters.scope_hint') }) : null,
        groups);
      const sheetCount = h('p', { class: 'fs-flt-sheet-count', 'aria-hidden': 'true' });
      const close = h('button', { type: 'button', class: 'fs-btn fs-btn--icon fs-dialog-close', 'aria-label': t('filters.close') }, h('span', { 'aria-hidden': 'true', text: '×' }));
      const seeAll = h('button', { type: 'button', class: 'fs-flt-see fs-btn fs-btn--primary' });
      const resetSheet = h('button', { type: 'button', class: 'fs-flt-reset fs-btn fs-btn--link', text: t('filters.reset') });
      const sheetBody = h('div', { class: 'fs-flt-sheet-body' });
      /* Volet du catalogue (fs-ui-dialog.css : latéral sur bureau, par le bas sur mobile ; bindDialog : Échap, fond, Tab). */
      const sheet = h('dialog', { class: 'fs-dialog fs-dialog--sheet fs-dialog--pinned fs-flt-sheet', 'aria-labelledby': sheetTitle },
        close,
        h('div', { class: 'fs-dialog-head fs-flt-sheet-head' }, h('h2', { class: 'fs-dialog-title fs-flt-sheet-title', id: sheetTitle, text: t('filters.filters') }), sheetCount),
        sheetBody,
        h('div', { class: 'fs-dialog-foot fs-flt-sheet-foot' }, resetSheet, seeAll));
      const sheetDialog = bindDialog(sheet, { restoreFocus: false });

      /* Colonne latérale (bureau) : en-tête (titre, nombre de filtres actifs, résultats, « Tout effacer »), rubrique
         « Jeu », puces actives et groupes. Lien d'évitement vers les annonces en premier (la colonne précède la grille dans
         l'ordre du clavier). */
      const sideTitle = nextId('colonne');
      const sideBadge = h('span', { class: 'fs-flt-badge', 'aria-hidden': 'true' });
      const sideCount = h('p', { class: 'fs-flt-side-count', 'aria-hidden': 'true' });
      const clearAll = h('button', { type: 'button', class: 'fs-flt-clear', text: t('filters.clear_all') });
      clearAll.addEventListener('click', () => { resetAll(); side.querySelector('.fs-flt-side-title')?.focus({ preventScroll: true }); });
      const skip = h('a', { class: 'fs-flt-skip', href: '#fs-catalog', text: t('filters.skip') });
      skip.addEventListener('click', (event) => {
        event.preventDefault();
        const first = grid.querySelector('[data-fs-listing]:not([hidden]) :is([data-fs-listing-link], [data-fs-open])');
        (first ?? ui?.search)?.focus();
      });
      const sideScroll = h('div', { class: 'fs-flt-side-scroll' });
      const side = h('div', { class: 'fs-flt-side', role: 'region', 'aria-labelledby': sideTitle },
        skip,
        h('div', { class: 'fs-flt-side-head' },
          h('h3', { class: 'fs-flt-side-title', id: sideTitle, tabindex: '-1' }, h('span', { text: t('filters.filters') }), sideBadge),
          clearAll, sideCount),
        sideScroll);

      let opener = null;
      function scrollSheetTo(el) {
        const offset = (groupNav?.isConnected ? groupNav.getBoundingClientRect().height : 0) + 8;
        sheetBody.scrollTo({ top: sheetBody.scrollTop + el.getBoundingClientRect().top - sheetBody.getBoundingClientRect().top - offset, behavior: 'instant' });
      }
      /* Ouvre le tiroir ; avec un identifiant de facette (raccourci du dock, « jeu »), son groupe est ouvert, la facette
         amenée sous le sommaire et son premier contrôle (le jeu courant) reçoit le focus. Sinon : haut du tiroir, focus
         sur « Fermer ». */
      openSheet = (facetId) => {
        if (sideMode()) return;
        opener = document.activeElement;
        if (!sheet.open) { sheetDialog.open(); pushSheetEntry(); }
        const target = typeof facetId === 'string' ? sheet.querySelector(`[data-fs-flt-facet="${CSS.escape(facetId)}"]`) : null;
        if (target) {
          const details = target.closest('details');
          if (details) details.open = true;
          if (target === gameNav) sheetBody.scrollTo({ top: 0, behavior: 'instant' }); else scrollSheetTo(target);
          target.querySelector('[aria-current="page"], input, button:not([aria-disabled="true"])')?.focus({ preventScroll: true });
        } else { sheetBody.scrollTo?.({ top: 0 }); close.focus(); }
      };
      more.addEventListener('click', () => openSheet());
      close.addEventListener('click', () => sheet.close());
      seeAll.addEventListener('click', () => { sheet.close(); scrollToResults(); });
      resetSheet.addEventListener('click', () => resetAll(resetSheet));
      sheet.addEventListener('close', () => {
        // Passage au bureau tiroir ouvert : le bouton d'ouverture (dock) est masqué, le focus va au titre de la colonne.
        if (sideMode()) side.querySelector('.fs-flt-side-title')?.focus({ preventScroll: true });
        else if (opener?.isConnected) opener.focus({ preventScroll: true });
        lastOpener = opener;
        // Fermé autrement que par « Précédent » (ou par le choix d'un jeu) : l'entrée d'historique du tiroir est retirée.
        if (sheetEntry && entryState().fsFltSheet) { sheetEntry = false; rewriteOnPop = true; history.back(); }
      });

      // Dock : loupe (barre collée) → la rangée de recherche redescend sous l'en-tête, clavier ouvert, résultats dessous.
      dockSearch.addEventListener('click', () => { host.setAttribute('data-fs-flt-search', ''); search.focus({ preventScroll: true }); });
      search.addEventListener('blur', () => setTimeout(() => { if (document.activeElement !== search) host.removeAttribute('data-fs-flt-search'); }, 120));
      search.addEventListener('keydown', (event) => { if (event.key === 'Enter' && !sideMode()) search.blur(); });
      // Frappe dans la barre collée : les résultats repartent du haut, juste sous la recherche (clavier ouvert en dessous).
      search.addEventListener('input', () => { if (host.hasAttribute('data-fs-flt-search')) scrollToResults({ instant: true }); });
      // Raccourci ou filtre actif touché dans le dock collé : retour au début des résultats, rangée ramenée au début.
      rail.addEventListener('click', (event) => {
        if (!event.target.closest?.('.fs-flt-suggest .fs-flt-chip:not(.fs-flt-chip--opener):not(.fs-flt-chip--all), .fs-flt-pill, .fs-flt-reset-all')) return;
        if (host.hasAttribute('data-fs-flt-stuck')) scrollToResults();
        rail.scrollTo({ left: 0, behavior: reducedMotion() ? 'instant' : 'smooth' });
      });

      // État vide utile : élargir le filtre qui rend le plus d'annonces, réinitialiser, proposer une annonce.
      const empty = h('div', { class: 'fs-empty fs-flt-empty', hidden: true });
      grid.after(empty);

      host.append(top, dock, sheet, side);
      ui = { search, sort, sortWrap, sortCurrent, visibleCount, badge, more, top, searchWrap, news, pills, dock, dockSearch, rail, suggest, allFilters,
        groupNav, groupsBox, sheet, sheetBody, sheetCount, seeAll, empty, side, sideScroll, sideBadge, sideCount, clearAll };
      controls.push({ update: renderChrome });
      place();
      // Jeu choisi dans la rubrique (colonne) : la rubrique s'efface sur l'écran du jeu, le focus passe à la recherche.
      if (focused && gameNav?.contains(focused) && !gameNav.isConnected) search.focus({ preventScroll: true });
      else keepFocus(focused);
    };
    let openSheet = () => {};

    /* Placement selon la largeur : les mêmes nœuds sont déplacés (aucun contrôle en double ; état, compteurs et focus
       gardés quand on passe d'une largeur à l'autre). Ordre du DOM = ordre de tabulation. */
    function place() {
      if (!ui) return;
      const focused = document.activeElement;
      const side = sideMode();
      const { top, searchWrap, sortWrap, news, more, visibleCount, pills, dock, dockSearch, rail, suggest, allFilters, sheet, sheetBody, groupNav, groupsBox, sideScroll } = ui;
      host.toggleAttribute('data-fs-flt-side', side);
      toolbar.hidden = !side;
      if (side) {
        if (sheet.open) sheet.close();
        top.replaceChildren(...[searchWrap, sortWrap, news, visibleCount].filter(Boolean));
        toolbar.replaceChildren(top);
        sideScroll.replaceChildren(...[gamesShown() ? gameNav : null, pills, groupsBox].filter(Boolean));
      } else {
        top.replaceChildren(searchWrap);
        host.prepend(top);
        more.append(visibleCount);
        dock.replaceChildren(dockSearch, more, rail);
        rail.replaceChildren(...[gameChip, pills, sortWrap, suggest, allFilters].filter(Boolean));
        sheetBody.replaceChildren(...[gamesShown() ? gameNav : null, groupNav, groupsBox].filter(Boolean));
      }
      if (live.parentElement !== host) host.append(live);
      keepFocus(focused);
      sideObserver?.disconnect();
      if (side) sideObserver?.observe(ui.side);
      stickTop = null;
      stick();
      measureDock();
    }

    /* ---------- Bureau : un seul défilement ----------
       La colonne plus haute que l'écran suit la page (son « top » collant descend d'autant qu'on défile), se fixe quand
       son bas atteint le bas de l'écran, et remonte de même jusqu'à se recoller sous l'en-tête. Plus courte que l'écran :
       collante en haut. Jamais de défilement interne. */
    const SIDE_MARGIN = 16;
    let stickTop = null;
    let stickBase = 0;
    let stickY = window.scrollY;
    function stick() {
      const y = window.scrollY;
      const dy = y - stickY;
      stickY = y;
      if (!ui || !host.hasAttribute('data-fs-flt-side')) return;
      const el = ui.side;
      if (stickTop === null) {
        el.style.removeProperty('--flt-side-dyn');
        stickBase = parseFloat(getComputedStyle(el).top) || 0;
        stickTop = stickBase;
      }
      const lowest = Math.min(stickBase, window.innerHeight - SIDE_MARGIN - el.offsetHeight);
      const next = Math.min(stickBase, Math.max(lowest, stickTop - dy));
      if (next !== stickTop || !el.style.getPropertyValue('--flt-side-dyn')) {
        stickTop = next;
        el.style.setProperty('--flt-side-dyn', `${next}px`);
      }
    }
    let stickFrame = 0;
    const stickSoon = () => { if (!stickFrame) stickFrame = requestAnimationFrame(() => { stickFrame = 0; stick(); }); };
    const sideObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(stickSoon) : null;
    addEventListener('scroll', stickSoon, { passive: true });
    addEventListener('resize', () => { stickTop = null; stickSoon(); });
    wide.addEventListener?.('change', () => { place(); if (lastResult && ui) renderChrome(lastResult); });

    /* ---------- Mobile et tablette : dock collant ----------
       La barre entière est collante, décalée vers le haut de la hauteur de la recherche (--flt-lead), qui passe sous
       l'en-tête ; seul le dock reste visible. --flt-anchor : bas de l'en-tête collant. */
    function measureDock() {
      if (!ui || sideMode()) { host.removeAttribute('data-fs-flt-stuck'); return; }
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--fs-header-h')) || 0;
      host.style.setProperty('--flt-anchor', `${Math.round(header)}px`);
      host.style.setProperty('--flt-lead', `${ui.dock.offsetTop}px`);
      onScroll();
    }
    let scrollFrame = 0;
    function onScroll() {
      scrollFrame = 0;
      if (!ui || sideMode()) return;
      const anchor = parseFloat(host.style.getPropertyValue('--flt-anchor')) || 0;
      const lead = parseFloat(host.style.getPropertyValue('--flt-lead')) || 0;
      host.toggleAttribute('data-fs-flt-stuck', host.getBoundingClientRect().top <= anchor - lead + 1 && window.scrollY > 0);
    }
    function scrollToResults({ instant = false } = {}) {
      if (!ui || sideMode()) {
        if (ui && host.getBoundingClientRect().top < 0) host.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
        return;
      }
      const dockBottom = ui.dock.getBoundingClientRect().bottom;
      // Début des résultats : la grille, ou l'élément posé juste avant elle qui l'ouvre ([data-fs-results-start] :
      // encart « Comparer 2 annonces » de fs-compare.js).
      const start = grid.previousElementSibling?.matches('[data-fs-results-start]') ? grid.previousElementSibling : grid;
      const gridTop = start.getBoundingClientRect().top;
      if (gridTop < dockBottom || gridTop > window.innerHeight * 0.6) window.scrollTo({ top: window.scrollY + gridTop - dockBottom - 12, behavior: instant || reducedMotion() ? 'instant' : 'smooth' });
    }
    window.addEventListener('scroll', () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(onScroll); }, { passive: true });
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => measureDock()).observe(host);

    /* « Précédent » ferme le tiroir (mobile et tablette) : une entrée d'historique à l'ouverture, position gardée (y,
       relu par for-sale.js au popstate). Sur l'entrée retrouvée, l'adresse est réécrite avec les filtres choisis dans le
       tiroir, et le focus revient au bouton qui l'a ouvert. */
    let sheetEntry = false;
    let rewriteOnPop = false;
    let lastOpener = null;
    function pushSheetEntry() {
      try {
        const current = { ...entryState() };
        delete current.fsFltSheet;
        history.replaceState({ ...current, y: Math.round(window.scrollY) }, '');
        history.pushState({ ...current, y: Math.round(window.scrollY), fsFltSheet: 1 }, '');
        sheetEntry = true;
      } catch { sheetEntry = false; }
    }
    /* Retour d'historique propre au tiroir (fermeture) : for-sale.js ne doit pas y relire l'adresse (syncFromUrl). */
    const sheetPop = () => rewriteOnPop || (sheetEntry && !entryState().fsFltSheet);
    window.addEventListener('popstate', () => {
      if (rewriteOnPop) rewriteOnPop = false;
      else if (sheetEntry && !entryState().fsFltSheet) { sheetEntry = false; if (ui?.sheet.open) ui.sheet.close(); }
      else return;
      writeUrl();
      // Jeu choisi dans le tiroir : entrée d'avant le tiroir réécrite ci-dessus, puis entrée du jeu poussée.
      if (pendingPick) {
        const code = pendingPick;
        pendingPick = null;
        gamePending = true;
        window.FSNav.pick(code, { from: gameChip });
        return;
      }
      // for-sale.js rend le focus de l'entrée retrouvée : celui du bouton qui a ouvert le tiroir passe après.
      setTimeout(() => { if (lastOpener?.isConnected) lastOpener.focus({ preventScroll: true }); }, 60);
    });

    const resetAll = (from) => {
      state = { ...state, q: '', f: {} };
      if (ui) ui.search.value = '';
      refresh();
      if (from && !from.isConnected) ui?.search.focus();
    };

    const removeFacet = (id) => {
      if (id === 'q') { state = { ...state, q: '' }; if (ui) ui.search.value = ''; }
      else { const f = { ...state.f }; delete f[id]; state = { ...state, f }; }
      refresh();
    };

    function renderChrome(result) {
      const { visibleCount, badge, pills, sheetCount, seeAll, empty, more, sortCurrent, sort } = ui;
      const sorted = engine.sorts.find((item) => item.id === result.state.sort);
      sortCurrent.textContent = sorted ? sorted.label : '';
      sort.parentElement.toggleAttribute('data-fs-flt-sorted', Boolean(sorted));
      const filtered = result.active.length > 0 || Boolean(result.state.q.trim());
      const published = totalOf(game);
      const outOf = filtered ? result.total : (Number.isSafeInteger(published) && published > result.total ? published : null);
      // Nombre (en gras) d'annonces, sur le total s'il y a lieu (« 7 annonces sur 14 », « 7 of 14 listings »).
      const counted = (total) => (total === null ? rich('filters.listings', { count: result.count }, ['count'])
        : rich('filters.listings_of', { count: result.count, total }, ['count']));
      // Dock : forme courte (« 7 sur 14 ») pour garder la rangée de raccourcis visible.
      if (!sideMode() && filtered) visibleCount.replaceChildren(...rich('filters.count_of_short', { count: result.count, total: result.total }, ['count']));
      else visibleCount.replaceChildren(...counted(outOf));
      sheetCount.textContent = filtered ? t('filters.listings_of', { count: result.count, total: result.total }) : listingsText(result.count);
      seeAll.textContent = result.count ? t('filters.see_listings', { count: result.count }) : t('filters.no_listing');
      badge.textContent = result.active.length ? String(result.active.length) : '';
      // Colonne latérale : nombre de filtres actifs (recherche comprise), résultats, « Tout effacer » seulement s'il sert.
      const activeTotal = result.active.length + (result.state.q.trim() ? 1 : 0);
      ui.sideBadge.textContent = activeTotal ? String(activeTotal) : '';
      ui.sideCount.replaceChildren(...counted(outOf));
      if (ui.clearAll.hidden === filtered) ui.clearAll.hidden = !filtered;
      more.setAttribute('aria-label', result.active.length ? t('filters.filters_with_active', { active: t('filters.filters_active', { count: result.active.length }) }) : t('filters.filters'));

      // Puces des filtres actifs (supprimables) et « Tout réinitialiser ».
      const focusedPill = pills.contains(document.activeElement) ? [...pills.querySelectorAll('button')].indexOf(document.activeElement) : -1;
      pills.replaceChildren();
      const entries = [];
      if (result.state.q.trim()) entries.push({ id: 'q', text: t('filters.query_pill', { query: result.state.q.trim() }) });
      for (const id of result.active) {
        const facet = engine.byId.get(id);
        const value = result.state.f[id];
        if (SET_TYPES.has(facet.type)) {
          for (const item of value) {
            const option = facet.meta.options.find((entry) => entry.value === item);
            entries.push({ id, text: option?.label ?? item, remove: () => setValue(facet, value.filter((other) => other !== item)), iconKey: option?.icon });
          }
        } else entries.push({ id, text: describeValue(facet, value), iconKey: facet.icon });
      }
      for (const entry of entries) {
        const button = h('button', { type: 'button', class: 'fs-flt-pill fs-ui-badge', 'aria-label': t('filters.remove_filter', { filter: entry.text }) },
          icon(entry.iconKey, 18), h('span', { text: entry.text }), h('span', { class: 'fs-flt-pill-x', 'aria-hidden': 'true', text: '×' }));
        button.addEventListener('click', () => {
          const index = [...pills.querySelectorAll('button')].indexOf(button);
          if (entry.remove) entry.remove(); else removeFacet(entry.id);
          const buttons = [...pills.querySelectorAll('button')];
          const next = buttons[Math.min(index, buttons.length - 1)];
          // Colonne latérale : dernière puce retirée → titre « Filtres » (pas de saut vers la recherche au-dessus de la grille).
          if (!next && host.hasAttribute('data-fs-flt-side')) ui.side.querySelector('.fs-flt-side-title')?.focus({ preventScroll: true });
          else (next ?? ui.search).focus();
        });
        pills.append(button);
      }
      if (entries.length) {
        const reset = h('button', { type: 'button', class: 'fs-flt-reset-all', text: t('filters.reset_all') });
        reset.addEventListener('click', () => { resetAll(); ui.search.focus(); });
        pills.append(reset);
      }
      pills.hidden = entries.length === 0;
      if (focusedPill >= 0) { const buttons = [...pills.querySelectorAll('button')]; buttons[Math.min(focusedPill, buttons.length - 1)]?.focus(); }

      // État vide (un jeu sans annonce garde le message de la section, voir syncGameEmpty).
      empty.hidden = result.count > 0 || !filtered || !engine.listings.length;
      if (!empty.hidden) {
        const suggestions = result.gains.filter((gain) => gain.count > 0).slice(0, 3).map((gain) => {
          const facet = engine.byId.get(gain.id);
          const text = gain.id === 'q' ? t('filters.widen_query', { query: result.state.q.trim() }) : t('filters.widen_filter', { filter: describeValue(facet, result.state.f[gain.id]) });
          const button = h('button', { type: 'button', class: 'fs-flt-widen fs-btn' },
            h('span', { text: t('filters.widen', { what: text }) }), h('span', { class: 'fs-flt-widen-n', text: t('filters.widen_count', { listings: listingsText(gain.count) }) }));
          button.addEventListener('click', () => { removeFacet(gain.id); ui.search.focus({ preventScroll: true }); });
          return h('li', {}, button);
        });
        const proposer = root.querySelector('[data-fs-propose-open]') && game !== 'all'
          ? h('a', { class: 'fs-flt-propose fs-btn fs-btn--link', href: '#sell', 'data-fs-propose-open': game, text: t('filters.propose', { game: gameName(game) }) }) : null;
        const reset = h('button', { type: 'button', class: 'fs-btn fs-btn--primary', text: t('filters.reset_all') });
        reset.addEventListener('click', () => { resetAll(); ui.search.focus({ preventScroll: true }); });
        empty.replaceChildren(
          h('p', { class: 'fs-empty-title fs-flt-empty-title', text: t('filters.empty_title') }),
          suggestions.length ? h('p', { class: 'fs-empty-text', text: t('filters.empty_text') }) : null,
          suggestions.length ? h('ul', { class: 'fs-flt-widen-list' }, suggestions) : null,
          h('div', { class: 'fs-empty-actions' }, reset, proposer),
        );
      }
    }

    /* ---------- Pagination et défilement continu ----------
       Toutes les annonces admises sont des cartes complètes de la page (aucune requête : la Section Rendering API ignore
       ?page=N pour cette section). `limit` résultats montrés (12 au bureau, 8 au mobile), remis à zéro à chaque changement
       de filtre, de tri ou de recherche. La suite apparaît d'elle-même quand la sentinelle (pied de la liste) approche à
       moins d'un écran, AUTO_PAGES fois au plus : le pied de page reste atteignable. Toujours là en repli (clavier,
       lecteur d'écran, mouvement réduit) : le bouton « Voir plus d'annonces ». Nombre montré / total annoncé par la zone
       vivante. Retour sur la liste (Précédent, rechargement) : nombre de cartes rétabli (sessionStorage, par adresse et
       état des filtres) ; la position est rétablie par for-sale.js (history.state.y). */
    if (grid.hasAttribute('data-fs-index-partial')) {
      console.warn('[Space Syndicate] catalogue : seuls les 250 premiers métaobjets sont lus par la page', grid.dataset.fsIndexCount);
    }
    const pager = h('div', { class: 'fs-pagination fs-pagination--more fs-flt-pager', hidden: true });
    const pagerStatus = h('p', { class: 'fs-pagination-status fs-flt-pager-status' });
    const pagerButton = h('button', { type: 'button', class: 'fs-flt-more-cards fs-btn', text: t('filters.more_listings') });
    const sentinel = h('div', { class: 'fs-flt-sentinel', 'aria-hidden': 'true' });
    pager.append(pagerStatus, pagerButton, sentinel);
    grid.after(pager);
    let counts = { shown: 0, total: 0 };
    let autoCount = 0;
    const passing = () => [...grid.querySelectorAll('[data-fs-listing]')].filter((card) => !card.hidden || card.hasAttribute('data-fs-page-out'));
    const renderPager = () => {
      const rest = counts.total - counts.shown;
      pager.hidden = rest <= 0;
      pagerStatus.textContent = rest > 0 ? t('filters.pager_status', counts) : '';
    };

    /* Nombre de cartes gardé pour le retour sur la liste (onglet seulement ; stockage indisponible : rien). */
    const STORE = 'fs-flt-pages';
    const storeKey = () => `${window.location.pathname}|${pageKey}`;
    const readStore = () => { try { return JSON.parse(sessionStorage.getItem(STORE) || '{}'); } catch { return {}; } };
    const remember = () => { try { sessionStorage.setItem(STORE, JSON.stringify(rememberLimit(readStore(), storeKey(), limit))); } catch { /* onglet sans stockage */ } };
    /* Retour sur la liste (Précédent, rechargement) : le premier rendu montre d'emblée autant de cartes qu'au départ (la
       page ne raccourcit jamais), avant que for-sale.js ne rétablisse la position au chargement complet de la page. */
    const navigation = (() => { try { return performance.getEntriesByType('navigation')[0]?.type || ''; } catch { return ''; } })();
    const returning = navigation === 'back_forward' || navigation === 'reload';

    /** Montre la page suivante (cartes déjà présentes) ; bouton : focus sur la première carte ajoutée.
        Filtres, tri et compteurs inchangés : seules les cartes de la page et le compteur de pages sont réécrits (la barre
        n'est pas redessinée ; défilement continu sans tâche longue sous Lenis). */
    const more = ({ auto = false } = {}) => {
      const list = passing();
      const target = Math.min(list.length, limit + pageSize());
      if (target <= limit) return;
      const before = new Set(list.slice(0, limit));
      if (auto) autoCount += 1;
      limit = target;
      if (lastResult) { paginate(lastResult); observer.takeRecords(); } else refresh({ keepPage: true });
      remember();
      if (!auto) passing().slice(0, limit).find((card) => !before.has(card))?.querySelector('[data-fs-listing-link], [data-fs-open]')?.focus({ preventScroll: true });
      announce(counts.total > counts.shown ? t('filters.pager_status', counts) : listingsText(counts.shown));
      setTimeout(autoLoad, 0);
    };
    pagerButton.addEventListener('click', () => more());
    /* Défilement continu : sentinelle à moins d'un écran sous le bas de la fenêtre, ou déjà dépassée (défilement rapide,
       touche Fin) ; position relue au moment du calcul. Observateur (mise en page qui change) et défilement (une lecture
       par image). Seulement une fois la page chargée et sa position rétablie (for-sale.js, au chargement) : les
       défilements du rétablissement ne révèlent rien ; avant, le bouton suffit. Jamais pendant un trajet (intention
       « navigate » de fs-scroll.js) : seul le visiteur qui fait défiler la liste lui-même en révèle la suite. */
    let autoReady = document.readyState === 'complete';
    if (!autoReady) window.addEventListener('load', () => setTimeout(() => { autoReady = true; autoLoad(); }, 0), { once: true });
    const autoLoad = () => {
      if (!autoReady || pager.hidden || autoCount >= AUTO_PAGES) return;
      const near = sentinel.getBoundingClientRect().top < window.innerHeight * 2;
      const navigating = window.FSScroll?.intent === 'navigate';
      if (shouldAutoLoad({ near, rest: counts.total - counts.shown, auto: autoCount, reduced: reducedMotion(), navigating })) more({ auto: true });
    };
    if (typeof IntersectionObserver === 'function') new IntersectionObserver(autoLoad, { rootMargin: '0px 0px 100% 0px' }).observe(sentinel);
    let autoFrame = 0;
    window.addEventListener('scroll', () => { if (!autoFrame) autoFrame = requestAnimationFrame(() => { autoFrame = 0; autoLoad(); }); }, { passive: true });

    function paginate(result) {
      const key = `${game}|${result.state.sort}|${result.state.q.trim()}|${JSON.stringify(result.state.f)}`;
      if (key !== pageKey) {
        pageKey = key;
        limit = (!booted && returning && restoredLimit(readStore(), { key: storeKey(), size: pageSize(), total: Infinity })) || pageSize();
        autoCount = 0;
      }
      let shown = 0;
      let total = 0;
      for (const card of grid.querySelectorAll('[data-fs-listing]')) {
        if (card.hidden && !card.hasAttribute('data-fs-page-out')) continue;
        total += 1;
        const out = total > limit;
        card.toggleAttribute('data-fs-page-out', out);
        if (card.hidden !== out) card.hidden = out;
        if (!out) shown += 1;
      }
      counts = { shown, total };
      renderPager();
    }

    /* ---------- Application (instantanée) ---------- */
    const applyOrder = (key) => {
      if (key === orderKey) return;
      const first = orderKey === '' && !booted;
      orderKey = key;
      // Premier rendu dans l'ordre publié : le DOM y est déjà, rien à déplacer (tri « Plus récentes » : on trie).
      if (first && !state.sort && !state.q.trim() && !engine.recent) return;
      const order = engine.order(state);
      const sequence = order.map((index) => engine.listings[index].card);
      if (game === 'all' || !state.sort && !state.q.trim()) {
        // Ordre publié : toutes les cartes (y compris des autres jeux) reprennent leur rang d'origine.
        const all = [...cards].sort((a, b) => Number(a.dataset.fsOrder || 0) - Number(b.dataset.fsOrder || 0));
        const set = new Set(sequence);
        grid.append(...(game === 'all' ? sequence : [...sequence, ...all.filter((card) => !set.has(card))]));
      } else grid.append(...sequence);
    };

    const refresh = ({ keepPage = false } = {}) => {
      if (!keepPage) pageKey = '';
      const started = performance.now();
      const result = engine.evaluate(state);
      state = { ...state, ...result.state, q: state.q };
      lastResult = result;
      const pass = new Map(engine.listings.map((listing, index) => [listing.card, result.visible[index]]));
      for (const card of cards) {
        const inGame = pass.has(card);
        const hide = !inGame || !pass.get(card);
        card.removeAttribute('data-fs-page-out');
        if (card.hidden !== hide) card.hidden = hide;
        card.toggleAttribute('data-fs-flt-out', inGame && !pass.get(card));
      }
      if (engine.listings.length) applyOrder(currentOrderKey());
      paginate(result);
      for (const control of controls) control.update(result);
      observer.takeRecords();
      lastDuration = performance.now() - started;
      host.dataset.fsFltMs = lastDuration.toFixed(2);
      scheduleUrl();
      announceResult(result);
    };
    const currentOrderKey = () => `${game}|${state.sort}|${state.sort ? '' : normalizeKey(state.q)}`;
    function announceResult(result) {
      if (!engine.listings.length) syncGameEmpty();
      else announce(result.count ? (result.active.length || result.state.q.trim() ? t('filters.listings_of', { count: result.count, total: result.total }) : listingsText(result.count)) : t('filters.no_match_short'));
    }

    /* ---------- Prix changé après le rendu (setPrice, fs-purchase.js) ----------
       Le prix ne compte que pour l'ordre des tris par prix (ni filtre ni compteur) : les changements sont regroupés, au
       plus un passage par image. Tri qui ne dépend pas du prix, ou ordre inchangé : rien à déplacer. Pendant un
       défilement, les cartes ne changent pas d'ordre, sauf si toutes les places touchées sont hors de l'écran (sous
       la fenêtre, ou au-delà des pages montrées) ; sinon le nouvel ordre attend la fin du défilement (scrollend, ou
       SCROLL_IDLE_MS sans défilement). Résultat final identique à un rafraîchissement complet. */
    const isPassing = (card) => !card.hidden || card.hasAttribute('data-fs-page-out');
    const flushPrices = ({ scrolling }) => {
      if (!engine || !lastResult) return undefined;
      const sorted = engine.sorts.find((item) => item.id === state.sort);
      if (sorted?.price && engine.listings.length) {
        const sequence = engine.order(state).map((index) => engine.listings[index].card);
        const members = new Set(sequence);
        const placed = [...grid.children].filter((card) => members.has(card));
        if (firstDifference(placed, sequence) >= 0) {
          const before = placed.filter(isPassing);
          const at = firstDifference(before, sequence.filter(isPassing));
          // Place touchée la plus haute : sous la fenêtre, ou hors des pages montrées → invisible, appliqué tout de suite.
          const hiddenMove = at < 0 || before[at].hasAttribute('data-fs-page-out') || before[at].getBoundingClientRect().top >= window.innerHeight;
          if (scrolling && !hiddenMove) return 'defer';
          placeSequence(grid, sequence);
          orderKey = currentOrderKey();
          paginate(lastResult);
          observer.takeRecords();
        }
      }
      announceResult(lastResult);
      return undefined;
    };
    const priceBatch = createFrameBatch(flushPrices, {
      frame: (callback) => requestAnimationFrame(callback), cancelFrame: (id) => cancelAnimationFrame(id),
      wait: (callback, ms) => setTimeout(callback, ms), cancelWait: (id) => clearTimeout(id), now: () => performance.now(),
    });
    addEventListener('scroll', () => priceBatch.scrolled(), { passive: true });
    addEventListener('scrollend', () => priceBatch.scrollEnded(), { passive: true });

    /* Changement de jeu (entrée par la rubrique « Jeu », une tuile, « Toutes les annonces », FSUniverse.set, historique) :
       filtres propres au jeu remis à zéro ; recherche gardée, tri gardé s'il existe dans le nouveau jeu. Retour
       d'historique : l'état de l'entrée, relu depuis l'adresse. L'adresse est réécrite aussitôt. */
    const switchGame = (next) => {
      const previous = state;
      const fromUrl = entryState().fsFltGame === next;
      game = next;
      engine = engineFor(game);
      orderKey = '';
      state = fromUrl ? urlState() : { q: previous.q, sort: engine.sorts.some((item) => item.id === previous.sort) ? previous.sort : '', f: {} };
      if (ui?.sheet?.open) ui.sheet.close();
      build();
      syncGames();
      gamePending = true;
      refresh();
      writeUrl();
    };

    /* Jeu sans annonce (écran du jeu) : aucune carte, message et actions au nom du jeu. for-sale.js montre et masque
       [data-fs-empty] ; ce module n'y écrit que le texte et le jeu du lien « Proposer ». */
    const sectionEmpty = root.querySelector('[data-fs-empty]');
    function syncGameEmpty() {
      const name = gameName(game);
      const text = sectionEmpty?.querySelector('[data-fs-empty-text]');
      const propose = sectionEmpty?.querySelector('[data-fs-empty-propose]');
      if (text) text.textContent = t('filters.game_none', { game: name });
      if (propose) { propose.setAttribute('data-fs-propose-open', game); propose.textContent = t('filters.propose', { game: name }); }
      gamePending = false;
      announce(t('filters.game_none_short', { game: name }));
    }
    sectionEmpty?.querySelector('[data-fs-empty-all]')?.addEventListener('click', (event) => {
      // « Voir toutes les annonces » : même entrée que « Tous les jeux » de la rubrique (position gardée).
      if (typeof window.FSNav?.pick === 'function') { gamePending = true; window.FSNav.pick('all', { from: linkOf('all') ?? event.currentTarget }); return; }
      root.querySelector('[data-fs-show-all]')?.click();
    });

    const observer = new MutationObserver((records) => {
      if (!records.some((record) => record.target.matches?.('[data-fs-offer]'))) return;
      const next = currentGame();
      if (next !== game) switchGame(next); else refresh({ keepPage: true });
    });

    /* Relit l'état depuis l'adresse (Précédent / Suivant : for-sale.js l'appelle au popstate) ; un autre jeu s'y installe
       d'abord. Sans effet pendant les retours propres au tiroir. */
    const syncFromUrl = () => {
      if (!ui || sheetPop()) return;
      const next = currentGame();
      if (next !== game) { switchGame(next); return; }
      state = urlState();
      ui.search.value = state.q;
      ui.sort.value = state.sort;
      refresh();
      writeUrl();
    };

    // État initial depuis l'adresse (?f, ?q, ?tri), pour le jeu posé par ?game=.
    game = currentGame();
    engine = engineFor(game);
    state = urlState();
    build();
    observer.observe(grid, { attributes: true, attributeFilter: ['hidden'], subtree: true });
    new MutationObserver(() => { const next = currentGame(); if (next !== game) switchGame(next); })
      .observe(root, { attributes: true, attributeFilter: ['data-fs-universe', 'data-fs-catalog-game'] });
    syncGames();
    refresh();
    writeUrl();
    booted = true;
    host.setAttribute('data-fs-flt-ready', '');
    root.setAttribute('data-fs-flt-ready', '');

    window.FSListingFilters = Object.freeze({
      state: () => JSON.parse(JSON.stringify({ game, ...state })),
      set: (partial = {}) => { state = { ...state, ...partial, f: partial.f ?? state.f }; if (ui) { ui.search.value = state.q; ui.sort.value = state.sort; } refresh(); },
      reset: () => resetAll(),
      syncFromUrl,
      /** Affiche les pages nécessaires pour que la carte de cette référence soit visible (fiche ouverte par l'adresse). */
      reveal: (ref) => {
        const card = cards.find((item) => item.dataset.fsRef === ref);
        if (!card?.hasAttribute('data-fs-page-out')) return Boolean(card && !card.hidden);
        const order = [...grid.querySelectorAll('[data-fs-listing]')].filter((item) => !item.hidden || item.hasAttribute('data-fs-page-out'));
        limit = Math.max(limit, order.indexOf(card) + 1);
        refresh({ keepPage: true });
        return !card.hidden;
      },
      /** Pagination : cartes montrées (limit), taille d'une page, pages chargées au défilement, état du chargement. */
      page: () => ({ limit, size: pageSize(), shown: counts.shown, total: counts.total, auto: autoCount }),
      /**
       * Prix d'une carte changé après le rendu (fs-purchase.js, état réel de l'achat, décision 024) : null quand l'achat est
       * impossible (carte rétrogradée, « sans prix affiché » pour le tri et le filtre), les centimes à la restauration.
       */
      setPrice: (ref, cents) => {
        const listing = prepared.find((item) => item.ref === ref);
        const price = Number.isSafeInteger(cents) && cents > 0 ? cents : null;
        if (!listing || listing.price === price) return false;
        listing.price = price;
        // Ordre des tris par prix mis à jour à l'image suivante, regroupé, jamais pendant un défilement (flushPrices).
        priceBatch.request();
        return true;
      },
      /** Face-à-face de deux annonces d'un même jeu (fs-compare.js) : { game, gameName, icons, result } ou null. */
      compare: (refA, refB) => {
        const pair = [refA, refB].map((ref) => prepared.find((listing) => listing.ref === ref));
        if (!pair[0] || !pair[1] || pair[0] === pair[1] || pair[0].game !== pair[1].game || !config.games[pair[0].game]) return null;
        const code = pair[0].game;
        return { game: code, gameName: gameName(code), icons: packIcons(code), result: compareListings(config.games[code], pair[0], pair[1]) };
      },
      /** Le jeu de cette annonce a-t-il un face-à-face (configuration de filtres) ? */
      comparable: (code) => Boolean(config.games[code]),
      duration: () => lastDuration,
      result: () => (lastResult ? { count: lastResult.count, total: lastResult.total, active: lastResult.active, gains: lastResult.gains } : null),
      measure: (runs = 20) => {
        const times = [];
        for (let run = 0; run < runs; run += 1) { refresh(); times.push(lastDuration); }
        return times.sort((a, b) => a - b);
      },
    });
  };

  const start = () => document.querySelectorAll('[data-fs-demo][data-fs-source="listings"]').forEach((root) => { try { boot(root); } catch (error) { console.warn('[Space Syndicate] filtres indisponibles', error); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
}
